// src/components/ManutenzioneTab.js
// Modulo Gestione Manutenzione — vive dentro la Commessa (come Cronoprogramma, Economico, ecc.)
// Gerarchia: Commessa → Impianti → Componenti (ad albero) → Piani di Manutenzione → Interventi
// Tutte le creazioni sono inline, nessun popup.
import React, { useState, useEffect } from "react";
import { collection, query, where, onSnapshot, addDoc, deleteDoc,
  doc, serverTimestamp, getDoc, getDocs, setDoc } from "firebase/firestore";
import { db } from "../firebase";
import { prossimoProgressivo } from "../utils/contatoriUtils";
import { useToast } from "../utils/Toast";
import { confirm } from "../utils/confirm";
import { logEvento } from "../utils/logEvento";
import { logModifica } from "../utils/logModifica";
import { classificaFasciaIntervento } from "../utils/noleggiUtils";
import { comprimiImmagine } from "../utils/imageUtils";
import { CAPITOLATI_STANDARD, FREQUENZE_VOCE } from "../utils/capitolatiStandard";
import { aggiungiMesi } from "../utils/verificheUtils";
import { Wrench, Plus, X, ChevronLeft, ChevronDown, Trash2, Printer } from "lucide-react";
import { toLocalDateStr } from "../utils/dateUtils";
import { numIt } from "../utils/economiaCommessa";
import FirmaPad, { FirmaImmagine } from "./FirmaPad";
import { chiaveBozza, prefissoBozze, documentoBozza, useBozzaModulo } from "../utils/bozzeLocali";
import AvvisoBozza, { ElencoBozze, delGiorno } from "./AvvisoBozza";
import { UsaMiaFirma } from "./MiaFirma";
import { useAuth } from "../contexts/AuthContext";
import { verificaModificaRapportino } from "../utils/bloccoRapportini";

export const FREQUENZE = {
  settimanale: { label: "Settimanale", giorni: 7 },
  mensile:     { label: "Mensile",     giorni: 30 },
  trimestrale: { label: "Trimestrale", giorni: 90 },
  semestrale:  { label: "Semestrale",  giorni: 180 },
  annuale:     { label: "Annuale",     giorni: 365 },
};
const ESITI = ["Risolto", "Parziale", "Non risolto", "Da riprogrammare"];
// Corrispondenza con le chiavi di STATI_INTERVENTO in RapportiniTab.js (badge colorato
// dell'esito nel Registro Rapportini) — vocabolari diversi per la stessa cosa altrimenti:
// senza questa mappa un intervento di manutenzione risolto non mostrava nessuna etichetta
// nel Registro Rapportini generale (nessun crash — un controllo esistente lo evitava — ma
// l'esito spariva silenziosamente dalla vista).
const ESITO_A_STATI_INTERVENTO = {
  "Risolto": "risolto", "Parziale": "parzialmente", "Non risolto": "non_risolto", "Da riprogrammare": "nuovo_intervento",
};

const inp = { border:"1px solid #e5e7eb", borderRadius:7, padding:"7px 10px", fontSize:13, fontFamily:"'Inter',sans-serif", outline:"none" };

// ─── Firma su canvas ──────────────────────────────────────────────────────────
// Firma: componente unico di tutta l'app (components/FirmaPad.js)
function FirmaCanvas({ onSave, onCancel, label }) {
  return <FirmaPad label={label || "Firma"} onSave={onSave} onCancel={onCancel}/>;
}

export function prossimaScadenza(piano, ultimoIntervento) {
  const base = ultimoIntervento?.data ? new Date(ultimoIntervento.data+"T00:00:00")
    : piano.decorrenza ? new Date(piano.decorrenza+"T00:00:00")
    : (piano.createdAt?.toDate ? piano.createdAt.toDate() : new Date());
  const giorni = FREQUENZE[piano.frequenza]?.giorni || 30;
  const next = new Date(base); next.setDate(next.getDate()+giorni);
  return next;
}

// Prossima scadenza di UNA SINGOLA voce di capitolato — cerca, tra tutti gli interventi
// già fatti su questo piano, l'ultima volta in cui QUESTA voce specifica è stata spuntata
// come fatta; se non è mai stata fatta, parte dalla decorrenza del piano. Ogni voce cammina
// per conto proprio: un "serraggio moduli" annuale e una "verifica differenziale" semestrale
// nello stesso capitolato non condividono mai la stessa scadenza.
//
// N.P. = voce NON PROGRAMMATA su questo impianto: è una proprietà del capitolato (np:true
// sulla voce in piano.voci), decisa da chi gestisce il piano, mai dal tecnico. Una voce N.P.
// non ha scadenza (restituisce null) e resta fuori da promemoria, email, Scadenzario e
// calendario. Si legge sempre dal piano, non dalla copia salvata nell'intervento, così un
// rapportino vecchio non può riaccendere o spegnere una voce. Quando una voce torna
// programmata (programmataDal), la cadenza riparte da quella data se è più recente
// dell'ultima esecuzione. Stessa logica in functions/index.js (prossimaScadenzaVoceCF).
export function prossimaScadenzaVoce(voce, piano, interventiPiano) {
  const vocePiano = (piano?.voci||[]).find(x=>x.id===voce.id) || voce;
  if (vocePiano.np) return null;
  let ultimaVoltaFatta = null;
  (interventiPiano||[]).forEach(iv => {
    (iv.checklistCompilata||[]).forEach(gruppo => {
      (gruppo.voci||[]).forEach(v => {
        if (v.id === voce.id && v.fatto && iv.data) {
          if (!ultimaVoltaFatta || iv.data > ultimaVoltaFatta) ultimaVoltaFatta = iv.data;
        }
      });
    });
  });
  let baseStr = ultimaVoltaFatta || piano.decorrenza
    || (piano.createdAt?.toDate ? toLocalDateStr(piano.createdAt.toDate()) : toLocalDateStr(new Date()));
  if (vocePiano.programmataDal && vocePiano.programmataDal > baseStr) baseStr = vocePiano.programmataDal;
  // aggiungiMesi (già usata per le Verifiche Periodiche di legge) gestisce correttamente
  // il caso di fine mese — es. 31 gennaio + 1 mese deve dare 28 febbraio, non "3 marzo" come
  // farebbe un ingenuo setMonth(). Un bug reale, testato e corretto qui, non ipotetico.
  return new Date(aggiungiMesi(baseStr, vocePiano.frequenzaMesi||voce.frequenzaMesi||12) + "T00:00:00");
}

// Stato sintetico di un piano a capitolato, per la lista piani e le schede riassuntive:
// prende la scadenza più vicina tra tutte le sue voci — è quella che conta davvero, non una
// media o una frequenza unica che nel capitolato non esiste più.
export function prossimaScadenzaCapitolato(piano, interventiPiano) {
  const voci = piano.voci||[];
  if (voci.length===0) return null;
  const date = voci.map(v => prossimaScadenzaVoce(v, piano, interventiPiano)).filter(Boolean);
  if (date.length===0) return null; // tutte le voci N.P.: nessuna scadenza
  return date.reduce((min,d) => d<min?d:min, date[0]);
}

// Copia leggibile della checklist da portare sul rapportino generato (senza foto: i
// documenti Firestore hanno un limite di dimensione e le foto restano nell'intervento).
function snapshotChecklist(checklistCompilata) {
  return (checklistCompilata||[]).flatMap(g => (g.voci||[]).map(v => ({
    gruppo: g.componenteNome||"", testo: v.testo, frequenzaMesi: v.frequenzaMesi||null,
    fatto: !!v.fatto, np: !!v.np, esito: v.esito||null,
    anomalia: v.anomalia||"", misure: v.misure||"",
  })));
}

function costruisciGruppiChecklist(componenti, piano) {
  // Capitolato: un unico elenco piatto, una voce per riga con la propria cadenza — niente
  // albero da percorrere, è proprio il punto di questa modalità.
  if (piano?.modalita === "capitolato") {
    return [{ componenteId:null, componenteNome:null, voci:(piano.voci||[]).map(v=>({id:v.id,testo:v.testo,frequenzaMesi:v.frequenzaMesi,np:!!v.np})) }];
  }
  const gruppi = [];
  if ((piano?.checklist||[]).length > 0) {
    gruppi.push({ componenteId:null, componenteNome:"Controlli generali del piano", voci:(piano.checklist||[]).map(v=>({id:v.id,testo:v.testo})) });
  }
  const percorri = parentId => {
    componenti.filter(c=>c.parentId===parentId).forEach(c=>{
      if ((c.checklist||[]).length>0) {
        gruppi.push({ componenteId:c.id, componenteNome:c.nome, voci:c.checklist.map(v=>({id:v.id,testo:v.testo})) });
      }
      percorri(c.id);
    });
  };
  percorri(null);
  return gruppi;
}

// ─── Riga input inline con checklist (riusata per Componente e Piano) ────────
function EditorChecklist({ checklist, setChecklist }) {
  const addRiga = () => setChecklist(c=>[...c, { id:Date.now().toString()+Math.random(), testo:"" }]);
  const updRiga = (id,val) => setChecklist(c=>c.map(r=>r.id===id?{...r,testo:val}:r));
  const rmRiga = id => setChecklist(c=>c.filter(r=>r.id!==id));
  return (
    <div>
      {checklist.length===0 && (
        <div style={{fontSize:10,color:"#9ca3af",marginBottom:6}}>
          Ogni voce sarà una riga da spuntare durante l'intervento. Es. "Verifica serraggio connettori", "Pulizia pannelli", "Controllo continuità cavi".
        </div>
      )}
      {checklist.map((r,i)=>(
        <div key={r.id} style={{display:"flex",gap:6,marginBottom:6}}>
          <input value={r.testo} onChange={e=>updRiga(r.id,e.target.value)} style={{...inp,flex:1}} placeholder={`Es. Voce di verifica ${i+1}`}/>
          <button type="button" onClick={()=>rmRiga(r.id)} style={{background:"none",border:"none",color:"#fca5a5",cursor:"pointer"}}><Trash2 size={14}/></button>
        </div>
      ))}
      <button type="button" onClick={addRiga} style={{fontSize:12,color:"#7c3aed",background:"none",border:"none",cursor:"pointer",fontWeight:600}}>+ Aggiungi voce checklist</button>
    </div>
  );
}

// ─── Form inline: Nuovo Impianto ─────────────────────────────────────────────
// Un impianto fisico (una cabina, un quadro) vive vent'anni e attraversa più commesse: può
// essere manutenuto da noi per tre anni, passare a un altro fornitore, poi tornare. Le
// commesse cambiano, l'impianto resta lo stesso.
//
// Per questo qui NON si crea una scheda isolata dentro la commessa: o si collega un impianto
// già censito nell'anagrafica del cliente, oppure lo si crea e finisce automaticamente anche
// lì. Così la stessa cabina è censita una volta sola, e le verifiche di legge da un lato e
// gli interventi contrattuali dall'altro parlano dello stesso oggetto.
function FormImpianto({ nomeSuggerito, onSalva, onAnnulla, impiantiAnagrafica = [], clienteNome }) {
  const [modo, setModo] = useState(impiantiAnagrafica.length > 0 ? "collega" : "nuovo");
  const [impiantoScelto, setImpiantoScelto] = useState("");
  const [nome, setNome] = useState(nomeSuggerito||"");
  const [tipo, setTipo] = useState("");

  const confermaCollegamento = () => {
    const imp = impiantiAnagrafica.find(i=>i.id===impiantoScelto);
    if (!imp) return;
    // Nome e tipologia si copiano solo per poterli leggere qui senza una seconda lettura:
    // la fonte vera resta impiantoAnagraficaId.
    onSalva({ nome: imp.denominazione, tipo: imp.tipologia||"", impiantoAnagraficaId: imp.id });
  };

  return (
    <div style={{background:"#faf5ff",border:"1px solid #e9d5ff",borderRadius:10,padding:14,marginBottom:14}}>
      {impiantiAnagrafica.length > 0 && (
        <div style={{display:"flex",gap:8,marginBottom:12}}>
          {[{k:"collega",label:`Collega un impianto già censito (${impiantiAnagrafica.length})`},
            {k:"nuovo",label:"È un impianto nuovo"}].map(b=>(
            <button key={b.k} onClick={()=>setModo(b.k)}
              style={{padding:"6px 12px",borderRadius:8,fontSize:11.5,fontWeight:700,cursor:"pointer",fontFamily:"'Inter',sans-serif",
                border:`1px solid ${modo===b.k?"#7c3aed":"#e5e7eb"}`, background:modo===b.k?"#7c3aed":"#fff", color:modo===b.k?"#fff":"#6b7280"}}>
              {b.label}
            </button>
          ))}
        </div>
      )}

      {modo==="collega" ? (
        <>
          <div style={{fontSize:10.5,color:"#6d28d9",marginBottom:8}}>
            💡 Collegando un impianto già in anagrafica{clienteNome?` di ${clienteNome}`:""}, gli interventi di questa commessa
            e le verifiche periodiche di legge resteranno agganciati allo stesso impianto — non a due schede separate.
          </div>
          <div style={{display:"grid",gridTemplateColumns:"1fr auto auto",gap:8,alignItems:"end"}}>
            <div>
              <label style={{fontSize:11,fontWeight:600,color:"#6b7280",display:"block",marginBottom:4}}>Impianto in anagrafica *</label>
              <select value={impiantoScelto} onChange={e=>setImpiantoScelto(e.target.value)} style={{...inp,width:"100%"}}>
                <option value="">— Seleziona —</option>
                {impiantiAnagrafica.map(i=>(
                  <option key={i.id} value={i.id}>
                    {i.denominazione}{i.ubicazione?` — ${i.ubicazione}`:""}{i.tipologia?` (${i.tipologia})`:""}
                  </option>
                ))}
              </select>
            </div>
            <button className="btn btn-primary" disabled={!impiantoScelto} onClick={confermaCollegamento}>Collega</button>
            <button className="btn btn-secondary" onClick={onAnnulla}>Annulla</button>
          </div>
        </>
      ) : (
        <>
          <div style={{fontSize:10.5,color:"#6d28d9",marginBottom:8}}>
            💡 Verrà creato anche nell'Anagrafica Impianti{clienteNome?` di ${clienteNome}`:""}, così resta disponibile
            anche per le commesse future e per le verifiche periodiche obbligatorie.
            {nomeSuggerito && " Il nome è precompilato con quello della commessa: modificalo se la commessa copre più impianti."}
          </div>
          <div style={{display:"grid",gridTemplateColumns:"2fr 1fr auto auto",gap:8,alignItems:"end"}}>
            <div>
              <label style={{fontSize:11,fontWeight:600,color:"#6b7280",display:"block",marginBottom:4}}>Nome impianto *</label>
              <input value={nome} onChange={e=>setNome(e.target.value)} style={{...inp,width:"100%"}} placeholder="Es. Cabina MT/BT stabilimento nord" autoFocus/>
            </div>
            <div>
              <label style={{fontSize:11,fontWeight:600,color:"#6b7280",display:"block",marginBottom:4}}>Tipologia</label>
              <input value={tipo} onChange={e=>setTipo(e.target.value)} style={{...inp,width:"100%"}} placeholder="Es. Elettrico, Antincendio..."/>
            </div>
            <button className="btn btn-primary" disabled={!nome.trim()} onClick={()=>onSalva({nome,tipo,creaInAnagrafica:true})}>Salva</button>
            <button className="btn btn-secondary" onClick={onAnnulla}>Annulla</button>
          </div>
        </>
      )}
    </div>
  );
}

// ─── Form inline: Nuovo Componente (con checklist) ───────────────────────────
function FormComponente({ parentNome, onSalva, onAnnulla }) {
  const [nome, setNome] = useState("");
  const [tipo, setTipo] = useState("");
  const [checklist, setChecklist] = useState([]);
  return (
    <div style={{background:"#faf5ff",border:"1px solid #e9d5ff",borderRadius:10,padding:14,marginTop:8,marginBottom:8}}>
      <div style={{fontSize:11,fontWeight:700,color:"#7c3aed",marginBottom:8}}>
        ➕ Nuovo componente{parentNome?` in "${parentNome}"`:""}
      </div>
      <div style={{display:"grid",gridTemplateColumns:"2fr 1fr",gap:8,marginBottom:8}}>
        <input value={nome} onChange={e=>setNome(e.target.value)} style={{...inp,width:"100%"}} placeholder="Es. Inverter INV-01, Stringa 1, Quadro QGBT..." autoFocus/>
        <input value={tipo} onChange={e=>setTipo(e.target.value)} style={{...inp,width:"100%"}} placeholder="Tipo (libero)"/>
      </div>
      <div style={{fontSize:11,fontWeight:600,color:"#6b7280",marginBottom:4}}>Checklist di verifica (facoltativa)</div>
      <EditorChecklist checklist={checklist} setChecklist={setChecklist}/>
      <div style={{display:"flex",gap:8,marginTop:10}}>
        <button className="btn btn-primary" disabled={!nome.trim()}
          onClick={()=>onSalva({nome,tipo,checklist:checklist.filter(r=>r.testo.trim())})}>Salva componente</button>
        <button className="btn btn-secondary" onClick={onAnnulla}>Annulla</button>
      </div>
    </div>
  );
}

// ─── Nodo ricorsivo dell'albero componenti ───────────────────────────────────
function NodoComponente({ componente, tutti, livello, espansi, setEspansi, onAggiungiFiglio, onElimina, onVediStorico, formAperto, onSalvaForm, onAnnullaForm }) {
  const figli = tutti.filter(c=>c.parentId===componente.id);
  const aperto = espansi[componente.id] !== false;
  const formQui = formAperto?.parentId === componente.id;
  return (
    <div style={{marginLeft: livello*20}}>
      <div style={{display:"flex",alignItems:"center",gap:6,padding:"6px 8px",borderRadius:6,background: livello===0?"#f9fafb":"transparent"}}>
        {figli.length>0 ? (
          <button onClick={()=>setEspansi(e=>({...e,[componente.id]:!aperto}))} style={{background:"none",border:"none",cursor:"pointer",padding:0,color:"#9ca3af"}}>
            <ChevronLeft size={13} style={{transform: aperto?"rotate(-90deg)":"rotate(0deg)",transition:"transform 0.15s"}}/>
          </button>
        ) : <span style={{width:13,display:"inline-block"}}/>}
        <span style={{fontSize:13,fontWeight: livello===0?700:500}}>{componente.nome}</span>
        {componente.tipo && <span style={{fontSize:10,color:"#7c3aed",background:"#faf5ff",padding:"1px 6px",borderRadius:8}}>{componente.tipo}</span>}
        <div style={{marginLeft:"auto",display:"flex",gap:4,alignItems:"center"}}>
          {(componente.checklist||[]).length>0 && (
            <button onClick={()=>onVediStorico(componente)}
              style={{fontSize:11,color:"#0369a1",background:"none",border:"none",cursor:"pointer",fontWeight:600}}>📜 Storico</button>
          )}
          <button onClick={()=>onAggiungiFiglio(componente.id, componente.nome)}
            style={{fontSize:11,color:"#7c3aed",background:"none",border:"none",cursor:"pointer",fontWeight:600}}>+ Sotto-elemento</button>
          <button onClick={()=>onElimina(componente)} style={{background:"none",border:"none",color:"#fca5a5",cursor:"pointer"}}><Trash2 size={12}/></button>
        </div>
      </div>
      {formQui && (
        <div style={{marginLeft:20}}>
          <FormComponente parentNome={componente.nome} onSalva={onSalvaForm} onAnnulla={onAnnullaForm}/>
        </div>
      )}
      {aperto && figli.map(f=>(
        <NodoComponente key={f.id} componente={f} tutti={tutti} livello={livello+1} espansi={espansi} setEspansi={setEspansi}
          onAggiungiFiglio={onAggiungiFiglio} onElimina={onElimina} onVediStorico={onVediStorico}
          formAperto={formAperto} onSalvaForm={onSalvaForm} onAnnullaForm={onAnnullaForm}/>
      ))}
    </div>
  );
}

// ─── Form inline: Nuovo Piano di Manutenzione ────────────────────────────────
// ─── Piano: scelta del capitolato standard, o modulo vuoto da compilare ──────
function ScegliCapitolato({ orgId, onScelto }) {
  const opzioni = Object.entries(CAPITOLATI_STANDARD);
  const [personalizzati, setPersonalizzati] = useState([]);
  useEffect(() => {
    if (!orgId) return;
    return onSnapshot(query(collection(db,"capitolatiPersonalizzati"), where("organizationId","==",orgId)),
      snap => setPersonalizzati(snap.docs.map(d=>({id:d.id,...d.data()})).sort((a,b)=>(a.nome||"").localeCompare(b.nome||"","it"))),
      // Se le regole non sono ancora pubblicate o la lettura fallisce, i modelli personali
      // semplicemente non compaiono: la scelta tra i modelli standard resta sempre disponibile.
      () => setPersonalizzati([]));
  }, [orgId]);
  return (
    <div style={{background:"#faf5ff",border:"1px solid #e9d5ff",borderRadius:10,padding:16,marginBottom:16}}>
      <div style={{fontSize:13,fontWeight:700,color:"#7c3aed",marginBottom:4}}>📋 Da quale modello parti?</div>
      <div style={{fontSize:11,color:"#6b7280",marginBottom:12}}>
        Un punto di partenza professionale — ogni voce resta modificabile, se ne possono togliere, aggiungere o correggere liberamente prima di salvare.
      </div>
      {personalizzati.length>0 && (
        <>
          <div style={{fontSize:11,fontWeight:700,color:"#374151",marginBottom:6}}>I tuoi modelli</div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(200px,1fr))",gap:8,marginBottom:12}}>
            {personalizzati.map(cap=>(
              <button key={cap.id} onClick={()=>onScelto(null,cap)}
                style={{textAlign:"left",padding:"12px 14px",borderRadius:8,border:"1px solid #e5e7eb",background:"#fff",cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>
                <div style={{fontSize:20,marginBottom:4}}>⭐</div>
                <div style={{fontSize:12,fontWeight:700,color:"#111827"}}>{cap.nome}</div>
                <div style={{fontSize:10,color:"#9ca3af",marginTop:2}}>{(cap.voci||[]).length} voci precompilate</div>
              </button>
            ))}
          </div>
          <div style={{fontSize:11,fontWeight:700,color:"#374151",marginBottom:6}}>Modelli standard Nexiora</div>
        </>
      )}
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(200px,1fr))",gap:8,marginBottom:8}}>
        {opzioni.map(([key,cap])=>(
          <button key={key} onClick={()=>onScelto(key)}
            style={{textAlign:"left",padding:"12px 14px",borderRadius:8,border:"1px solid #e5e7eb",background:"#fff",cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>
            <div style={{fontSize:20,marginBottom:4}}>{cap.icona}</div>
            <div style={{fontSize:12,fontWeight:700,color:"#111827"}}>{cap.nome}</div>
            <div style={{fontSize:10,color:"#9ca3af",marginTop:2}}>{cap.voci.length} voci precompilate</div>
          </button>
        ))}
        <button onClick={()=>onScelto("vuoto")}
          style={{textAlign:"left",padding:"12px 14px",borderRadius:8,border:"1px dashed #d1d5db",background:"#fafafa",cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>
          <div style={{fontSize:20,marginBottom:4}}>📝</div>
          <div style={{fontSize:12,fontWeight:700,color:"#374151"}}>Modulo vuoto</div>
          <div style={{fontSize:10,color:"#9ca3af",marginTop:2}}>Scrivo io tutte le voci</div>
        </button>
      </div>
    </div>
  );
}

// Editor delle voci di capitolato — ogni voce ha la propria cadenza indipendente, mai
// un'unica frequenza per tutto il piano: un serraggio annuale e una verifica semestrale
// convivono nello stesso capitolato senza bisogno di due piani separati.
function EditorCapitolato({ voci, setVoci }) {
  const upd = (i,k,v) => setVoci(voci.map((r,idx)=>idx===i?{...r,[k]:v}:r));
  const add = () => setVoci([...voci, { id:`v_${Date.now()}${Math.random()}`, testo:"", frequenzaMesi:12 }]);
  const rm = (i) => setVoci(voci.filter((_,idx)=>idx!==i));
  return (
    <div>
      {voci.map((v,i)=>(
        <div key={v.id} style={{display:"flex",gap:6,marginBottom:6,alignItems:"center"}}>
          <input value={v.testo} onChange={e=>upd(i,"testo",e.target.value)} placeholder="Es. Serraggio morsetti quadro generale"
            style={{...inp,flex:1,color:v.np?"#9ca3af":undefined}}/>
          <select value={v.frequenzaMesi} onChange={e=>upd(i,"frequenzaMesi",Number(e.target.value))} disabled={!!v.np}
            title={v.np?"Voce non programmata: la cadenza resta memorizzata e riparte se la voce torna programmata":""}
            style={{...inp,width:130,opacity:v.np?.45:1}}>
            {Object.entries(FREQUENZE_VOCE).map(([m,l])=><option key={m} value={m}>{l}</option>)}
          </select>
          <button type="button" onClick={()=>upd(i,"np",!v.np)}
            title="N.P. — voce non programmata su questo impianto: niente scadenza né promemoria, sul rapportino risulta N.P."
            style={{...inp,width:52,padding:"7px 0",cursor:"pointer",fontWeight:800,fontSize:11,
              background:v.np?"#4b5563":"#fff",color:v.np?"#fff":"#9ca3af",borderColor:v.np?"#4b5563":"#e5e7eb"}}>
            N.P.
          </button>
          <button onClick={()=>rm(i)} style={{background:"none",border:"none",cursor:"pointer",color:"#dc2626",padding:4}}><X size={14}/></button>
        </div>
      ))}
      <button onClick={add} style={{fontSize:11,color:"#7c3aed",background:"none",border:"none",cursor:"pointer",fontWeight:600,padding:"4px 0"}}>+ Aggiungi voce</button>
      {voci.length>0 && (
        <div style={{fontSize:10,color:"#9ca3af",marginTop:2}}>
          <strong>N.P.</strong> = voce non programmata su questo impianto (es. il modello prevede l'UPS ma l'impianto non ce l'ha): resta sul rapportino già segnata N.P., senza scadenza né promemoria.
        </div>
      )}
    </div>
  );
}

// ─── Salva come modello — l'azienda costruisce nel tempo la propria libreria di
// capitolati riutilizzabili, oltre ai 4 standard di Nexiora (che restano sempre
// disponibili, invariati). Utile per chi opera in settori diversi dall'elettrico/FTV
// (allarmi, climatizzazione, ascensori...) dove i modelli standard non si applicano.
function SalvaComeModello({ orgId, voci, onChiudi }) {
  const { addToast } = useToast();
  const [nome, setNome] = useState("");
  const [salvando, setSalvando] = useState(false);
  const vociValide = voci.filter(v=>v.testo.trim());
  return (
    <div style={{background:"#fff",border:"1px solid #e5e7eb",borderRadius:8,padding:12,marginBottom:8}}>
      <div style={{fontSize:11,fontWeight:700,color:"#374151",marginBottom:6}}>Salva le {vociValide.length} voci attuali come modello riutilizzabile</div>
      <div style={{display:"flex",gap:8}}>
        <input value={nome} onChange={e=>setNome(e.target.value)} placeholder="Nome del modello, es. Antintrusione standard" style={{...inp,flex:1}} autoFocus/>
        <button className="btn btn-primary" disabled={!nome.trim()||vociValide.length===0||salvando} onClick={async()=>{
          setSalvando(true);
          try {
            await addDoc(collection(db,"capitolatiPersonalizzati"), {
              organizationId: orgId, nome: nome.trim(),
              voci: vociValide.map(v=>({testo:v.testo, frequenzaMesi:v.frequenzaMesi})),
              createdAt: serverTimestamp(),
            });
            addToast("Modello salvato — disponibile da ora per ogni nuovo piano ⭐","success");
            onChiudi();
          } catch(err) { addToast(err.message||"Errore nel salvataggio del modello","error"); setSalvando(false); }
        }}>Salva modello</button>
        <button className="btn btn-secondary" onClick={onChiudi}>Annulla</button>
      </div>
    </div>
  );
}

function FormPiano({ orgId, onSalva, onAnnulla }) {
  const [capitolatoScelto, setCapitolatoScelto] = useState(null); // null finché non si sceglie
  const [mostraSalvaModello, setMostraSalvaModello] = useState(false);
  const [form, setForm] = useState({ nome:"", descrizione:"", frequenza:"mensile", priorita:"media", tecniciAltro:"", note:"", decorrenza:"", durataPrevista:"1" });
  const [checklist, setChecklist] = useState([]);
  const [voci, setVoci] = useState([]);
  const [dipendenti, setDipendenti] = useState([]);
  const [tecniciSel, setTecniciSel] = useState([]); // [{uid,nome}]

  useEffect(() => {
    if (!orgId) return;
    return onSnapshot(query(collection(db,"users"), where("organizationId","==",orgId), where("enabled","==",true)),
      snap => setDipendenti(snap.docs.map(d=>d.data()).sort((a,b)=>(a.displayName||"").localeCompare(b.displayName||"","it"))));
  }, [orgId]);

  const toggleTecnico = (u) => setTecniciSel(sel =>
    sel.find(t=>t.uid===u.uid) ? sel.filter(t=>t.uid!==u.uid) : [...sel, {uid:u.uid, nome:u.displayName}]
  );

  if (!capitolatoScelto) {
    return <ScegliCapitolato orgId={orgId} onScelto={(key,personalizzato)=>{
      const cap = key ? CAPITOLATI_STANDARD[key] : personalizzato;
      setVoci(cap ? cap.voci.map((v,i)=>({ id:`v_${Date.now()}_${i}`, testo:v.testo, frequenzaMesi:v.frequenzaMesi })) : []);
      if (cap && !form.nome.trim()) setForm(f=>({...f, nome:`Manutenzione — ${cap.nome}`}));
      setCapitolatoScelto(key || personalizzato.id);
    }}/>;
  }

  return (
    <div style={{background:"#faf5ff",border:"1px solid #e9d5ff",borderRadius:10,padding:16,marginBottom:16}}>
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:10}}>
        <div style={{fontSize:13,fontWeight:700,color:"#7c3aed"}}>🛠️ Nuovo Piano di Manutenzione</div>
        <button onClick={()=>setCapitolatoScelto(null)} style={{fontSize:11,color:"#6b7280",background:"none",border:"none",cursor:"pointer"}}>← Cambia modello</button>
      </div>
      <div style={{marginBottom:8}}>
        <label style={{fontSize:11,fontWeight:600,color:"#6b7280",display:"block",marginBottom:4}}>Nome piano *</label>
        <input value={form.nome} onChange={e=>setForm(f=>({...f,nome:e.target.value}))} style={{...inp,width:"100%"}} placeholder="Es. Manutenzione ordinaria impianto FTV — il nome del programma ricorrente, non del singolo intervento" autoFocus/>
      </div>
      <div style={{marginBottom:8}}>
        <label style={{fontSize:11,fontWeight:600,color:"#6b7280",display:"block",marginBottom:4}}>Descrizione</label>
        <textarea value={form.descrizione} onChange={e=>setForm(f=>({...f,descrizione:e.target.value}))} rows={2} style={{...inp,width:"100%",resize:"vertical"}}/>
      </div>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:10,marginBottom:8}}>
        <div>
          <label style={{fontSize:11,fontWeight:600,color:"#6b7280",display:"block",marginBottom:4}}>Priorità</label>
          <select value={form.priorita} onChange={e=>setForm(f=>({...f,priorita:e.target.value}))} style={{...inp,width:"100%"}}>
            <option value="bassa">Bassa</option><option value="media">Media</option><option value="alta">Alta</option>
          </select>
        </div>
        <div>
          <label style={{fontSize:11,fontWeight:600,color:"#6b7280",display:"block",marginBottom:4}}>Decorrenza (1ª scadenza) *</label>
          <input type="date" value={form.decorrenza} onChange={e=>setForm(f=>({...f,decorrenza:e.target.value}))} style={{...inp,width:"100%"}}/>
        </div>
        <div>
          <label style={{fontSize:11,fontWeight:600,color:"#6b7280",display:"block",marginBottom:4}}>Durata prevista (giorni)</label>
          <input type="number" min="1" step="1" value={form.durataPrevista} onChange={e=>setForm(f=>({...f,durataPrevista:e.target.value}))} style={{...inp,width:"100%"}} placeholder="1"/>
        </div>
      </div>
      <div style={{marginBottom:8}}>
        <label style={{fontSize:11,fontWeight:600,color:"#6b7280",display:"block",marginBottom:4}}>Tecnici necessari</label>
        {dipendenti.length===0 ? (
          <div style={{fontSize:11,color:"#9ca3af"}}>Nessun dipendente attivo trovato nell'organizzazione.</div>
        ) : (
          <div style={{display:"flex",flexWrap:"wrap",gap:6,marginBottom:6}}>
            {dipendenti.map(u=>{
              const sel = !!tecniciSel.find(t=>t.uid===u.uid);
              return (
                <button type="button" key={u.uid} onClick={()=>toggleTecnico(u)}
                  style={{padding:"4px 10px",borderRadius:20,border:`1px solid ${sel?"#7c3aed":"#e5e7eb"}`,
                    background:sel?"#ede9fe":"#fff",color:sel?"#7c3aed":"#6b7280",fontSize:11,fontWeight:600,cursor:"pointer"}}>
                  {sel?"✓ ":""}{u.displayName}
                </button>
              );
            })}
          </div>
        )}
        <input value={form.tecniciAltro} onChange={e=>setForm(f=>({...f,tecniciAltro:e.target.value}))} style={{...inp,width:"100%"}}
          placeholder="Altro (es. ditta esterna, competenze richieste non presenti in elenco...)"/>
      </div>
      <div style={{marginBottom:8}}>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:4}}>
          <div style={{fontSize:11,fontWeight:600,color:"#6b7280"}}>
            Voci del capitolato — ciascuna con la propria cadenza
          </div>
          <button onClick={()=>setMostraSalvaModello(true)} style={{fontSize:10,color:"#7c3aed",background:"none",border:"none",cursor:"pointer",fontWeight:600}}>
            ⭐ Salva come modello riutilizzabile
          </button>
        </div>
        <EditorCapitolato voci={voci} setVoci={setVoci}/>
      </div>
      {mostraSalvaModello && (
        <SalvaComeModello orgId={orgId} voci={voci} onChiudi={()=>setMostraSalvaModello(false)}/>
      )}
      <div style={{marginBottom:12}}>
        <label style={{fontSize:11,fontWeight:600,color:"#6b7280",display:"block",marginBottom:4}}>Note</label>
        <textarea value={form.note} onChange={e=>setForm(f=>({...f,note:e.target.value}))} rows={2} style={{...inp,width:"100%",resize:"vertical"}}/>
      </div>
      <div style={{display:"flex",gap:8}}>
        <button className="btn btn-primary" disabled={!form.nome.trim() || !form.decorrenza || voci.filter(v=>v.testo.trim()).length===0}
          onClick={()=>onSalva({...form, tecnici: tecniciSel, modalita:"capitolato", voci: voci.filter(v=>v.testo.trim())})}>Crea Piano</button>
        <button className="btn btn-secondary" onClick={onAnnulla}>Annulla</button>
      </div>
      {!form.decorrenza && <div style={{fontSize:10,color:"#dc2626",marginTop:4}}>La data di decorrenza è obbligatoria: serve per calcolare la prima scadenza di ogni voce.</div>}
      {voci.filter(v=>v.testo.trim()).length===0 && <div style={{fontSize:10,color:"#dc2626",marginTop:4}}>Serve almeno una voce nel capitolato.</div>}
    </div>
  );
}

// ─── Piano ad albero componenti — modalità già esistente, invariata: per chi vuole
// tracciare le singole apparecchiature una per una, resta disponibile così com'era. ─
function FormPianoComponenti({ orgId, onSalva, onAnnulla }) {
  const [form, setForm] = useState({ nome:"", descrizione:"", frequenza:"mensile", priorita:"media", tecniciAltro:"", note:"", decorrenza:"", durataPrevista:"1" });
  const [checklist, setChecklist] = useState([]);
  const [dipendenti, setDipendenti] = useState([]);
  const [tecniciSel, setTecniciSel] = useState([]); // [{uid,nome}]

  useEffect(() => {
    if (!orgId) return;
    return onSnapshot(query(collection(db,"users"), where("organizationId","==",orgId), where("enabled","==",true)),
      snap => setDipendenti(snap.docs.map(d=>d.data()).sort((a,b)=>(a.displayName||"").localeCompare(b.displayName||"","it"))));
  }, [orgId]);

  const toggleTecnico = (u) => setTecniciSel(sel =>
    sel.find(t=>t.uid===u.uid) ? sel.filter(t=>t.uid!==u.uid) : [...sel, {uid:u.uid, nome:u.displayName}]
  );

  return (
    <div style={{background:"#faf5ff",border:"1px solid #e9d5ff",borderRadius:10,padding:16,marginBottom:16}}>
      <div style={{fontSize:13,fontWeight:700,color:"#7c3aed",marginBottom:10}}>🛠️ Nuovo Piano di Manutenzione (ad albero componenti)</div>
      <div style={{marginBottom:8}}>
        <label style={{fontSize:11,fontWeight:600,color:"#6b7280",display:"block",marginBottom:4}}>Nome piano *</label>
        <input value={form.nome} onChange={e=>setForm(f=>({...f,nome:e.target.value}))} style={{...inp,width:"100%"}} placeholder="Es. Manutenzione ordinaria impianto FTV — il nome del programma ricorrente, non del singolo intervento" autoFocus/>
      </div>
      <div style={{marginBottom:8}}>
        <label style={{fontSize:11,fontWeight:600,color:"#6b7280",display:"block",marginBottom:4}}>Descrizione</label>
        <textarea value={form.descrizione} onChange={e=>setForm(f=>({...f,descrizione:e.target.value}))} rows={2} style={{...inp,width:"100%",resize:"vertical"}}/>
      </div>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr 1fr",gap:10,marginBottom:8}}>
        <div>
          <label style={{fontSize:11,fontWeight:600,color:"#6b7280",display:"block",marginBottom:4}}>Frequenza</label>
          <select value={form.frequenza} onChange={e=>setForm(f=>({...f,frequenza:e.target.value}))} style={{...inp,width:"100%"}}>
            {Object.entries(FREQUENZE).map(([k,v])=><option key={k} value={k}>{v.label}</option>)}
          </select>
        </div>
        <div>
          <label style={{fontSize:11,fontWeight:600,color:"#6b7280",display:"block",marginBottom:4}}>Priorità</label>
          <select value={form.priorita} onChange={e=>setForm(f=>({...f,priorita:e.target.value}))} style={{...inp,width:"100%"}}>
            <option value="bassa">Bassa</option><option value="media">Media</option><option value="alta">Alta</option>
          </select>
        </div>
        <div>
          <label style={{fontSize:11,fontWeight:600,color:"#6b7280",display:"block",marginBottom:4}}>Decorrenza (1ª scadenza) *</label>
          <input type="date" value={form.decorrenza} onChange={e=>setForm(f=>({...f,decorrenza:e.target.value}))} style={{...inp,width:"100%"}}/>
        </div>
        <div>
          <label style={{fontSize:11,fontWeight:600,color:"#6b7280",display:"block",marginBottom:4}}>Durata prevista (giorni)</label>
          <input type="number" min="1" step="1" value={form.durataPrevista} onChange={e=>setForm(f=>({...f,durataPrevista:e.target.value}))} style={{...inp,width:"100%"}} placeholder="1"/>
        </div>
      </div>
      <div style={{marginBottom:8}}>
        <label style={{fontSize:11,fontWeight:600,color:"#6b7280",display:"block",marginBottom:4}}>Tecnici necessari</label>
        {dipendenti.length===0 ? (
          <div style={{fontSize:11,color:"#9ca3af"}}>Nessun dipendente attivo trovato nell'organizzazione.</div>
        ) : (
          <div style={{display:"flex",flexWrap:"wrap",gap:6,marginBottom:6}}>
            {dipendenti.map(u=>{
              const sel = !!tecniciSel.find(t=>t.uid===u.uid);
              return (
                <button type="button" key={u.uid} onClick={()=>toggleTecnico(u)}
                  style={{padding:"4px 10px",borderRadius:20,border:`1px solid ${sel?"#7c3aed":"#e5e7eb"}`,
                    background:sel?"#ede9fe":"#fff",color:sel?"#7c3aed":"#6b7280",fontSize:11,fontWeight:600,cursor:"pointer"}}>
                  {sel?"✓ ":""}{u.displayName}
                </button>
              );
            })}
          </div>
        )}
        <input value={form.tecniciAltro} onChange={e=>setForm(f=>({...f,tecniciAltro:e.target.value}))} style={{...inp,width:"100%"}}
          placeholder="Altro (es. ditta esterna, competenze richieste non presenti in elenco...)"/>
      </div>
      <div style={{marginBottom:8}}>
        <div style={{fontSize:11,fontWeight:600,color:"#6b7280",marginBottom:4}}>Controlli generali (facoltativi, non legati a un componente specifico)</div>
        <EditorChecklist checklist={checklist} setChecklist={setChecklist}/>
      </div>
      <div style={{marginBottom:12}}>
        <label style={{fontSize:11,fontWeight:600,color:"#6b7280",display:"block",marginBottom:4}}>Note</label>
        <textarea value={form.note} onChange={e=>setForm(f=>({...f,note:e.target.value}))} rows={2} style={{...inp,width:"100%",resize:"vertical"}}/>
      </div>
      <div style={{display:"flex",gap:8}}>
        <button className="btn btn-primary" disabled={!form.nome.trim() || !form.decorrenza}
          onClick={()=>onSalva({...form, tecnici: tecniciSel, modalita:"componenti", checklist: checklist.filter(r=>r.testo.trim())})}>Crea Piano</button>
        <button className="btn btn-secondary" onClick={onAnnulla}>Annulla</button>
      </div>
      {!form.decorrenza && <div style={{fontSize:10,color:"#dc2626",marginTop:4}}>La data di decorrenza è obbligatoria: serve per calcolare la prima scadenza.</div>}
    </div>
  );
}

// ─── Form inline: Nuovo Intervento ───────────────────────────────────────────
// ── Registra chiamata — modulo veloce per chi risponde al telefono (segreteria,
// titolare, chiunque) e non è detto sia chi andrà poi a fare l'intervento: due righe,
// salva, e il tecnico completerà il resto più tardi da "📝 Completa". Nessuna checklist,
// nessuna firma qui — quelle si compilano solo al momento del vero intervento.
function FormRegistraChiamata({ onAnnulla, onSalva }) {
  const adesso = (() => {
    const d = new Date(); d.setSeconds(0,0);
    d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
    return d.toISOString().slice(0,16);
  })();
  const [richiestaAt, setRichiestaAt] = useState(adesso);
  const [note, setNote] = useState("");
  const [salvando, setSalvando] = useState(false);

  return (
    <div style={{background:"#fff",border:"1px solid #e5e7eb",borderRadius:10,padding:16,marginBottom:16,maxWidth:420}}>
      <div style={{fontSize:14,fontWeight:700,marginBottom:12}}>📞 Registra chiamata</div>
      <div style={{marginBottom:12}}>
        <label style={{fontSize:11,fontWeight:600,color:"#6b7280",display:"block",marginBottom:4}}>Chiamata ricevuta il</label>
        <input type="datetime-local" value={richiestaAt} onChange={e=>setRichiestaAt(e.target.value)} style={{...inp,width:"100%"}}/>
      </div>
      <div style={{marginBottom:14}}>
        <label style={{fontSize:11,fontWeight:600,color:"#6b7280",display:"block",marginBottom:4}}>Cosa ha segnalato il cliente</label>
        <textarea value={note} onChange={e=>setNote(e.target.value)} rows={3} placeholder="Es. Allarme che suona in modo intermittente dalle 14:00 circa..." style={{...inp,width:"100%",resize:"vertical"}}/>
      </div>
      <div style={{fontSize:10,color:"#9ca3af",marginBottom:14}}>Il tecnico che andrà sul posto troverà questa richiesta tra gli interventi "Da completare" e potrà chiuderla con checklist, esito e firme.</div>
      <div style={{display:"flex",gap:8}}>
        <button className="btn btn-primary" disabled={salvando} onClick={async()=>{
          setSalvando(true);
          await onSalva({ richiestaAt, note });
        }}>{salvando?"Salvataggio...":"💾 Registra chiamata"}</button>
        <button className="btn btn-secondary" onClick={onAnnulla}>Annulla</button>
      </div>
    </div>
  );
}

// ─── Modifica capitolato — aggiunge/toglie/corregge voci su un piano già esistente,
// senza perdere lo storico: le voci già presenti mantengono lo stesso id, quindi gli
// interventi già registrati restano collegati correttamente alla loro storia. Le voci
// rimosse spariscono solo dalle prossime visite — gli interventi passati che le
// riguardavano restano intatti nel loro rapportino originale, mai riscritti.
function ModificaCapitolato({ piano, onSalva, onAnnulla }) {
  const [voci, setVoci] = useState((piano.voci||[]).map(v=>({...v})));
  const [salvando, setSalvando] = useState(false);
  return (
    <div style={{background:"#faf5ff",border:"1px solid #e9d5ff",borderRadius:10,padding:16,margin:"0 24px 16px"}}>
      <div style={{fontSize:13,fontWeight:700,color:"#7c3aed",marginBottom:4}}>✏️ Modifica capitolato — {piano.nome}</div>
      <div style={{fontSize:11,color:"#6b7280",marginBottom:12}}>
        Le voci già esistenti restano collegate alla loro storia; una voce rimossa non tocca gli interventi già registrati in passato. Segna <strong>N.P.</strong> le voci non programmate su questo impianto: se in futuro tornano programmate, la loro cadenza riparte dalla data della modifica.
      </div>
      <EditorCapitolato voci={voci} setVoci={setVoci}/>
      <div style={{display:"flex",gap:8,marginTop:12}}>
        <button className="btn btn-primary" disabled={salvando || voci.filter(v=>v.testo.trim()).length===0}
          onClick={async()=>{
            setSalvando(true);
            const oggiStr = toLocalDateStr(new Date());
            const finali = voci.filter(v=>v.testo.trim()).map(v=>{
              const prima = (piano.voci||[]).find(x=>x.id===v.id);
              const out = { ...v, np: !!v.np };
              // Voce che torna programmata: la cadenza riparte da oggi, non da un'esecuzione
              // di anni fa (che la farebbe risultare subito scaduta).
              if (prima?.np && !v.np) out.programmataDal = oggiStr;
              return out;
            });
            await onSalva(finali);
          }}>
          {salvando?"Salvataggio...":"💾 Salva capitolato"}
        </button>
        <button className="btn btn-secondary" onClick={onAnnulla}>Annulla</button>
      </div>
      {voci.filter(v=>v.testo.trim()).length===0 && <div style={{fontSize:10,color:"#dc2626",marginTop:4}}>Serve almeno una voce nel capitolato.</div>}
    </div>
  );
}

// ─── Storico per singola voce — quando è stata fatta nel tempo, per notare un problema
// ricorrente prima che diventi serio (es. la stessa anomalia segnalata a ogni verifica).
function StoricoCapitolato({ piano, interventiPiano, onChiudi }) {
  const storicoVoce = (voce) => {
    const date = [];
    (interventiPiano||[]).forEach(iv => {
      (iv.checklistCompilata||[]).forEach(g => {
        (g.voci||[]).forEach(v => { if (v.id===voce.id && v.fatto && iv.data) date.push(iv); });
      });
    });
    return date.sort((a,b)=>(b.data||"").localeCompare(a.data||""));
  };
  return (
    <div style={{background:"#fff",border:"1px solid #e5e7eb",borderRadius:8,padding:16,margin:"0 24px 16px"}}>
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:12}}>
        <div style={{fontSize:13,fontWeight:700}}>📊 Storico del capitolato — {piano.nome}</div>
        <button onClick={onChiudi} style={{background:"none",border:"none",cursor:"pointer",color:"#6b7280"}}><X size={16}/></button>
      </div>
      {(piano.voci||[]).map(voce=>{
        const storico = storicoVoce(voce);
        return (
          <div key={voce.id} style={{marginBottom:12,paddingBottom:10,borderBottom:"1px solid #f3f4f6"}}>
            <div style={{fontSize:12,fontWeight:700,color:"#374151"}}>{voce.testo}
              <span style={{fontSize:10,color:"#9ca3af",fontWeight:400,marginLeft:6}}>({voce.np?"N.P. — non programmata":(FREQUENZE_VOCE[voce.frequenzaMesi]||`${voce.frequenzaMesi} mesi`)})</span>
            </div>
            {storico.length===0 ? (
              <div style={{fontSize:11,color:"#9ca3af",marginTop:3}}>Mai eseguita finora.</div>
            ) : (
              <div style={{display:"flex",flexWrap:"wrap",gap:6,marginTop:4}}>
                {storico.map(iv=>(
                  <span key={iv.id} style={{fontSize:10,color:"#059669",background:"#f0fdf4",padding:"2px 8px",borderRadius:10}}>
                    ✅ {iv.data.split("-").reverse().join("/")}
                  </span>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// Casella quadrata grande abbastanza da essere toccata con i guanti — usata per le quattro
// colonne di ogni voce (Effettuato, N.P., Esito positivo, Esito negativo).
function CellaVoce({ attivo, colore, sfondo, onClick, titolo, simbolo="✓", disabilitato=false }) {
  return (
    <button type="button" role="checkbox" aria-checked={!!attivo} aria-disabled={disabilitato} title={titolo}
      onClick={disabilitato?undefined:onClick}
      style={{width:30,height:30,borderRadius:7,border:`1.5px solid ${attivo?colore:"#d1d5db"}`,background:attivo?sfondo:(disabilitato?"#f9fafb":"#fff"),
        color:colore,fontWeight:800,fontSize:14,cursor:disabilitato?"default":"pointer",opacity:disabilitato&&!attivo?.45:1,display:"flex",alignItems:"center",justifyContent:"center",padding:0,margin:"0 auto"}}>
      {attivo?simbolo:""}
    </button>
  );
}

// Rapportino del piano — l'elenco completo delle voci con le quattro colonne (Effettuato,
// N.P., Esito positivo, Esito negativo), visibile aprendo il piano, prima ancora di iniziare
// un intervento. Mostra l'ultimo stato registrato per ogni voce e la sua scadenza; da qui si
// stampa anche il modulo vuoto da compilare a mano sul posto (cartaceo).
function RapportinoPiano({ gruppi, piano, interventiPiano, onStampaVuoto }) {
  const [aperto, setAperto] = useState(true);
  const isCap = piano?.modalita === "capitolato";
  const ordinati = [...(interventiPiano||[])].sort((a,b)=>(b.data||"").localeCompare(a.data||""));
  const ultimoStato = (voceId) => {
    for (const iv of ordinati) {
      for (const g of (iv.checklistCompilata||[])) {
        // N.P. non è un esito d'intervento: viene dal capitolato, quindi qui contano solo
        // le esecuzioni vere (un vecchio N.P. spuntato a mano non va mostrato come stato).
        const v = (g.voci||[]).find(x=>x.id===voceId && (x.fatto||x.esito));
        if (v) return { ...v, data: iv.data };
      }
    }
    return null;
  };
  const totale = gruppi.reduce((n,g)=>n+g.voci.length,0);
  const colonne = "minmax(0,1fr) repeat(4, 42px)";
  const casella = (on, colore, simbolo="✓") => (
    <div style={{width:26,height:26,margin:"0 auto",borderRadius:6,border:`1.5px solid ${on?colore:"#e5e7eb"}`,
      background:on?"#fff":"#fafafa",color:colore,fontWeight:800,fontSize:13,display:"flex",alignItems:"center",justifyContent:"center"}}>
      {on?simbolo:""}
    </div>
  );
  return (
    <div style={{background:"#fff",border:"1px solid #e5e7eb",borderRadius:10,padding:14,marginBottom:16}}>
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:8,flexWrap:"wrap",marginBottom:aperto?8:0}}>
        <button onClick={()=>setAperto(a=>!a)} style={{background:"none",border:"none",cursor:"pointer",fontSize:13,fontWeight:800,color:"#1e1b4b",padding:0}}>
          {aperto?"▾":"▸"} 📋 Rapportino del piano <span style={{fontWeight:500,color:"#9ca3af",fontSize:11}}>— {totale} voci</span>
        </button>
        <button onClick={onStampaVuoto}
          style={{fontSize:11,fontWeight:700,color:"#7c3aed",background:"#faf5ff",border:"1px solid #ddd6fe",borderRadius:7,padding:"5px 11px",cursor:"pointer"}}>
          🖨️ Stampa modulo vuoto (cartaceo)
        </button>
      </div>
      {aperto && (totale===0 ? (
        <div style={{fontSize:12,color:"#9ca3af"}}>Nessuna voce in questo piano{isCap?": usa \"Modifica capitolato\" per aggiungerne.":"."}</div>
      ) : (
        <>
          <div style={{fontSize:10,color:"#9ca3af",marginBottom:6}}>
            Ultimo stato registrato per ogni voce. Le caselle si compilano quando registri un intervento (<strong>Nuovo intervento</strong>).
          </div>
          {gruppi.map((g,gi)=>(
            <div key={g.componenteId||gi} style={{marginBottom:8}}>
              {g.componenteNome && <div style={{fontSize:11,fontWeight:700,color:"#7c3aed",marginBottom:3}}>{g.componenteNome}</div>}
              <div style={{display:"grid",gridTemplateColumns:colonne,gap:2,fontSize:8.5,fontWeight:800,color:"#6b7280",textTransform:"uppercase",textAlign:"center",padding:"0 0 3px 6px"}}>
                <div style={{textAlign:"left"}}>Voce</div><div>Effett.</div><div>N.P.</div><div style={{color:"#059669"}}>Esito +</div><div style={{color:"#dc2626"}}>Esito −</div>
              </div>
              {g.voci.map(v=>{
                const ult = v.np ? null : ultimoStato(v.id);
                let scad = null;
                if (isCap && v.np) scad = {t:"non programmata",c:"#6b7280"};
                else if (isCap && v.frequenzaMesi) {
                  const d = prossimaScadenzaVoce(v, piano, interventiPiano||[]);
                  const oggi = new Date(); oggi.setHours(0,0,0,0);
                  const gg = Math.ceil((d-oggi)/(1000*60*60*24));
                  scad = gg<0 ? {t:`scaduta da ${Math.abs(gg)}gg`,c:"#dc2626"} : gg<=14 ? {t:`tra ${gg}gg`,c:"#d97706"} : {t:d.toLocaleDateString("it-IT"),c:"#9ca3af"};
                }
                return (
                  <div key={v.id} style={{display:"grid",gridTemplateColumns:colonne,alignItems:"center",gap:2,padding:"4px 0 4px 6px",borderTop:"1px solid #f3f4f6",
                    background:ult?.esito==="negativo"?"#fef2f2":"transparent"}}>
                    <div style={{minWidth:0}}>
                      <div style={{fontSize:12.5,lineHeight:1.25,color:v.np?"#9ca3af":"#374151"}}>{v.testo}</div>
                      <div style={{display:"flex",flexWrap:"wrap",gap:6,marginTop:2,fontSize:9.5,color:"#9ca3af"}}>
                        {v.frequenzaMesi && !v.np && <span style={{background:"#f3f4f6",padding:"0 6px",borderRadius:8}}>{FREQUENZE_VOCE[v.frequenzaMesi]||`${v.frequenzaMesi} mesi`}</span>}
                        {scad && <span style={{fontWeight:700,color:scad.c}}>{v.np?scad.t:`prossima: ${scad.t}`}</span>}
                        {ult && <span>ultimo: {String(ult.data||"").split("-").reverse().join("/")}</span>}
                      </div>
                    </div>
                    {casella(!!ult?.fatto,"#374151")}
                    {casella(!!v.np,"#6b7280","–")}
                    {casella(ult?.esito==="positivo","#059669")}
                    {casella(ult?.esito==="negativo","#dc2626","✕")}
                  </div>
                );
              })}
            </div>
          ))}
        </>
      ))}
    </div>
  );
}

function FormIntervento({ piano, componenti, interventiPiano, esistente, sitoPredefinito, logoAssente, onAnteprima, onSalva, onAnnulla, riprendiBozza = false, projectId }) {
  const { addToast } = useToast();
  const { currentUser } = useAuth();
  const [gruppi, setGruppi] = useState(() => {
    if (esistente?.checklistCompilata?.length) {
      const copia = esistente.checklistCompilata.map(g=>({...g, voci:g.voci.map(v=>({...v}))}));
      // Un intervento già completato resta com'era (è un documento chiuso). Uno ancora da
      // completare si riallinea al capitolato attuale: N.P. si decide lì, non nel rapportino.
      if (esistente.stato==="completato") return copia;
      const vociPiano = piano?.modalita==="capitolato" ? (piano.voci||[]) : null;
      return copia.map(g=>({...g, voci:g.voci.map(v=>{
        const np = vociPiano ? !!vociPiano.find(x=>x.id===v.id)?.np : false;
        const inPiano = vociPiano ? vociPiano.some(x=>x.id===v.id) : true;
        if (!inPiano) return v;
        return np ? {...v, np:true, fatto:false, esito:null, anomalia:"", misure:""} : {...v, np:false};
      })}));
    }
    return costruisciGruppiChecklist(componenti, piano).map(g => ({ ...g, voci: g.voci.map(v => ({ ...v, fatto:false, np:!!v.np })) }));
  });
  const [note, setNote] = useState(esistente?.note||"");
  const [esito, setEsito] = useState(esistente?.esito||"");
  const [oreLavorate, setOreLavorate] = useState(esistente?.oreLavorate||"");
  const [materiali, setMateriali] = useState(esistente?.materiali||"");
  const [misure, setMisure] = useState(esistente?.misure||[]);
  const [firmaTecnico, setFirmaTecnico] = useState(esistente?.firmaTecnico||null);
  const [firmaCliente, setFirmaCliente] = useState(esistente?.firmaCliente||null);
  const [mostraFirmaTec, setMostraFirmaTec] = useState(false);
  const [mostraFirmaCli, setMostraFirmaCli] = useState(false);
  // Quando è arrivata la richiesta (facoltativo — solo per interventi su chiamata, non per
  // le visite di manutenzione programmata, dove il concetto di "tempo di risposta" non si
  // applica). Serve a confrontare davvero il tempo promesso da contratto con quello reale.
  const [richiestaAt, setRichiestaAt] = useState(esistente?.richiestaAt||"");
  // Sito in cui si esegue la manutenzione: lo stesso cliente può avere più capannoni,
  // uffici o abitazioni. Si propone l'ubicazione registrata sull'impianto, ma resta
  // sempre modificabile — è chi va sul posto a sapere dove si trova davvero.
  const [sito, setSito] = useState(esistente?.sito ?? sitoPredefinito ?? "");
  // Bozza automatica sul dispositivo (utils/bozzeLocali.js): se il telefono chiude la pagina
  // a metà compilazione, l'intervento si ritrova com'era.
  const bozza = useBozzaModulo({
    chiave: chiaveBozza(currentUser?.uid, "manutenzione", projectId, `${piano?.id}~${esistente?.id || "nuovo"}`),
    valore: { gruppi, note, esito, oreLavorate, materiali, misure, firmaTecnico, firmaCliente, richiestaAt, sito },
    info: { data: esistente?.data || toLocalDateStr(new Date()), pianoId: piano?.id, impiantoId: piano?.impiantoId, pianoNome: piano?.nome || "",
      titolo: `${esistente ? "Intervento da completare" : "Nuovo intervento"} — piano «${piano?.nome || ""}»`,
      vai: { page: "project", projectId, initialTab: "manutenzione" } },
    applica: d => {
      if (Array.isArray(d.gruppi)) setGruppi(d.gruppi);
      if (d.note !== undefined) setNote(d.note);
      if (d.esito !== undefined) setEsito(d.esito);
      if (d.oreLavorate !== undefined) setOreLavorate(d.oreLavorate);
      if (d.materiali !== undefined) setMateriali(d.materiali);
      if (Array.isArray(d.misure)) setMisure(d.misure);
      if (d.firmaTecnico !== undefined) setFirmaTecnico(d.firmaTecnico);
      if (d.firmaCliente !== undefined) setFirmaCliente(d.firmaCliente);
      if (d.richiestaAt !== undefined) setRichiestaAt(d.richiestaAt);
      if (d.sito !== undefined) setSito(d.sito);
    },
    riprendiSubito: riprendiBozza,
  });

  // Quattro colonne per voce: Effettuato (fatto) · N.P. (np) · Esito positivo / negativo.
  // N.P. = NON PROGRAMMATA su questo impianto: arriva già impostata dal capitolato del piano
  // e il tecnico non può cambiarla (né spuntare altro su quella voce). Una voce programmata
  // che oggi non si riesce a fare si lascia vuota: resta in scadenza, com'è giusto.
  // Scegliere un esito implica "effettuato"; togliere "effettuato" azzera l'esito.
  const updVoce = (gIdx, vId, patch) => setGruppi(gs => gs.map((g,i)=> i!==gIdx ? g : {...g, voci:g.voci.map(v=>v.id===vId?{...v,...patch}:v)}));
  const toggleEffettuato = (gi,v) => { if (v.np) return; updVoce(gi, v.id, v.fatto ? {fatto:false, esito:null} : {fatto:true}); };
  const setEsitoVoce = (gi,v,e) => { if (v.np) return; updVoce(gi, v.id, v.esito===e ? {esito:null} : {esito:e, fatto:true}); };
  const voceRisolta = v => !!(v.fatto || v.np);
  const isCapitolato = piano?.modalita === "capitolato";

  // Urgenza per voce (solo capitolato: ogni voce ha la propria cadenza, quindi la propria
  // scadenza) — usata sia per il pallino colorato sia per decidere quali voci sono
  // davvero "da fare oggi": un serraggio annuale fatto due mesi fa non deve bloccare la
  // chiusura di una visita mensile che riguarda tutt'altro.
  const urgenzaVoce = (v) => {
    if (!isCapitolato) return null;
    if (v.np) return {label:"non programmata", colore:"#9ca3af", urgente:false};
    const scad = prossimaScadenzaVoce(v, piano, interventiPiano||[]);
    if (!scad) return {label:"non programmata", colore:"#9ca3af", urgente:false};
    const oggi = new Date(); oggi.setHours(0,0,0,0);
    const giorni = Math.ceil((scad-oggi)/(1000*60*60*24));
    if (giorni<0) return {label:`scaduta da ${Math.abs(giorni)}gg`, colore:"#dc2626", urgente:true};
    if (giorni<=14) return {label:`tra ${giorni}gg`, colore:"#d97706", urgente:true};
    return {label:scad.toLocaleDateString("it-IT"), colore:"#9ca3af", urgente:false};
  };

  const totaleVoci = gruppi.reduce((s,g)=>s+g.voci.length,0);
  // Nel capitolato basta aver spuntato le voci davvero scadute o in scadenza — non tutte:
  // è proprio il punto di avere una cadenza per voce invece che un'unica lista sempre intera.
  // Nell'albero componenti (nessuna cadenza per voce) resta come prima: tutto va spuntato.
  const voceNonFatta = isCapitolato
    ? gruppi.some(g=>g.voci.some(v=>!voceRisolta(v) && urgenzaVoce(v)?.urgente))
    : gruppi.some(g=>g.voci.some(v=>!voceRisolta(v)));
  const vociSenzaEsito = gruppi.some(g=>g.voci.some(v=>v.fatto && !v.esito));
  const vociNegative = gruppi.flatMap((g,gi)=>g.voci.filter(v=>v.esito==="negativo").map(v=>({gi,v})));
  const negativeSenzaDescrizione = vociNegative.some(({v})=>!(v.anomalia||"").trim());
  const mancanti = [];
  if (!firmaTecnico) mancanti.push("Firma Tecnico");
  if (totaleVoci>0 && voceNonFatta) mancanti.push("Checklist incompleta");
  if (vociSenzaEsito) mancanti.push("Esito delle voci effettuate");
  if (negativeSenzaDescrizione) mancanti.push("Descrizione degli esiti negativi");
  if (!esito) mancanti.push("Esito intervento");

  const costruisciDati = () => ({
    checklistCompilata: gruppi, note, esito, oreLavorate, materiali, misure, firmaTecnico, firmaCliente, sito: sito.trim(),
    stato: mancanti.length===0 ? "completato" : "da_completare", mancanti, richiestaAt: richiestaAt||null, haEsitiNegativi: vociNegative.length>0,
  });

  const addMisura = () => setMisure(m=>[...m, {id:Date.now().toString()+Math.random(), label:"", valore:""}]);
  const updMisura = (id,campo,val) => setMisure(m=>m.map(r=>r.id===id?{...r,[campo]:val}:r));
  const rmMisura = id => setMisure(m=>m.filter(r=>r.id!==id));

  return (
    <div style={{background:"#fff",border:"1px solid #e5e7eb",borderRadius:10,padding:16,marginBottom:16}}>
      <div style={{fontSize:14,fontWeight:700,marginBottom:12}}>
        {esistente ? `📝 Completa intervento del ${esistente.data.split("-").reverse().join("/")}` : "🔧 Nuovo intervento"} — {piano.nome}
      </div>
      {bozza.trovata && <AvvisoBozza testo={esistente ? "Hai modifiche non salvate a questo intervento" : "Hai un intervento iniziato e non salvato"}
        salvataIl={bozza.trovata.salvataIl} onRiprendi={bozza.riprendi} onElimina={bozza.scarta} />}

      {gruppi.length===0 && (
        <div style={{background:"#fffbeb",border:"1px solid #fde68a",borderRadius:8,padding:"8px 12px",marginBottom:14,fontSize:11,color:"#92400e"}}>
          ℹ️ Nessuna checklist trovata: né il piano né i componenti dell'impianto hanno voci di verifica configurate. Puoi comunque completare l'intervento con note ed esito.
        </div>
      )}
      <div style={{marginBottom:14}}>
        <label style={{fontSize:11,fontWeight:600,color:"#6b7280",display:"block",marginBottom:4}}>Richiesta ricevuta il — facoltativo, solo per interventi su chiamata</label>
        <input type="datetime-local" value={richiestaAt} onChange={e=>setRichiestaAt(e.target.value)} style={{...inp,width:280}}/>
        <div style={{fontSize:10,color:"#9ca3af",marginTop:3}}>Compilalo solo se questo intervento nasce da una chiamata del cliente — serve a confrontare il tempo di risposta reale con quello promesso da contratto. Lascialo vuoto per una visita di manutenzione programmata.</div>
      </div>
      <div style={{marginBottom:14}}>
        <label style={{fontSize:11,fontWeight:600,color:"#6b7280",display:"block",marginBottom:4}}>Sito dell'intervento</label>
        <input value={sito} onChange={e=>setSito(e.target.value)} style={{...inp,width:"100%"}}
          placeholder="Dove si trova l'impianto: es. Capannone B, via Roma 12 — piano 1"/>
        {logoAssente && (
          <div style={{fontSize:10,color:"#b45309",marginTop:4}}>
            Nessun logo aziendale caricato: il rapporto stampato non lo mostrerà. Caricalo da Impostazioni Organizzazione.
          </div>
        )}
      </div>
      {gruppi.length>0 && (
        <div style={{marginBottom:14}}>
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:6}}>
            <div style={{fontSize:12,fontWeight:700,color:"#374151"}}>
              {isCapitolato ? "Voci del capitolato" : "Checklist"} ({gruppi.reduce((s,g)=>s+g.voci.filter(voceRisolta).length,0)}/{totaleVoci})
            </div>
            {(voceNonFatta || vociSenzaEsito) && (
              <button onClick={()=>setGruppi(gs=>gs.map(g=>({...g, voci:g.voci.map(v=>
                (!v.np && (isCapitolato ? urgenzaVoce(v)?.urgente : true) && (!v.fatto || !v.esito)) ? {...v,fatto:true,esito:v.esito||"positivo"} : v)})))}
                style={{fontSize:11,fontWeight:700,color:"#059669",background:"#f0fdf4",border:"1px solid #bbf7d0",borderRadius:7,padding:"4px 10px",cursor:"pointer"}}>
                ✅ Tutto regolare
              </button>
            )}
          </div>
          <div style={{fontSize:10,color:"#9ca3af",marginBottom:8}}>
            Per ogni voce: <strong>Effettuato</strong>, poi l'esito, <strong>positivo</strong> o <strong>negativo</strong>. <strong>N.P.</strong> (non programmata su questo impianto) arriva già segnata dal capitolato del piano e non si modifica qui; una voce che oggi non si riesce a fare si lascia vuota e resta in scadenza.
            {isCapitolato && " In rosso le voci scadute, in arancio quelle in scadenza entro 14 giorni."} "Tutto regolare" segna effettuato con esito positivo {isCapitolato ? "le voci scadute o in scadenza" : "tutte le voci"} non già impostate; una voce che non va, correggila a mano.
          </div>
          {gruppi.map((g,gi)=>{
            const voci = isCapitolato
              ? [...g.voci].sort((a,b)=>{
                  const ua=urgenzaVoce(a), ub=urgenzaVoce(b);
                  return (ua.urgente===ub.urgente) ? 0 : (ua.urgente?-1:1);
                })
              : g.voci;
            const colonne = "minmax(0,1fr) repeat(4, 42px)";
            return (
            <div key={g.componenteId||"generale"} style={{marginBottom:10,paddingBottom:8,borderBottom:"1px solid #f3f4f6"}}>
              {g.componenteNome && <div style={{fontSize:11,fontWeight:700,color:"#7c3aed",marginBottom:4}}>{g.componenteNome}</div>}
              <div style={{display:"grid",gridTemplateColumns:colonne,alignItems:"end",gap:2,padding:"0 0 4px 8px",fontSize:8.5,fontWeight:800,color:"#6b7280",textTransform:"uppercase",letterSpacing:.3,textAlign:"center"}}>
                <div style={{textAlign:"left"}}>Voce</div><div>Effett.</div><div>N.P.</div><div style={{color:"#059669"}}>Esito +</div><div style={{color:"#dc2626"}}>Esito −</div>
              </div>
              {voci.map(r=>{
                const urg = urgenzaVoce(r);
                const negativa = r.esito==="negativo";
                return (
                <div key={r.id} style={{display:"grid",gridTemplateColumns:colonne,alignItems:"center",gap:2,padding:"4px 0 4px 8px",borderRadius:6,background:negativa?"#fef2f2":"transparent"}}>
                  <div style={{minWidth:0}}>
                    <div style={{fontSize:13,lineHeight:1.25,overflowWrap:"anywhere",color:r.np?"#9ca3af":(negativa?"#b91c1c":(r.fatto?"#059669":"#374151")),textDecoration:r.np?"line-through":"none"}}>{r.testo}</div>
                    <div style={{display:"flex",flexWrap:"wrap",alignItems:"center",gap:5,marginTop:2}}>
                      {r.frequenzaMesi && <span style={{fontSize:9,color:"#9ca3af",background:"#f3f4f6",padding:"0 6px",borderRadius:8}}>{FREQUENZE_VOCE[r.frequenzaMesi]||`${r.frequenzaMesi} mesi`}</span>}
                      {urg && !voceRisolta(r) && <span style={{fontSize:9.5,fontWeight:700,color:urg.colore}}>{urg.label}</span>}
                      {r.foto ? (
                        <img src={r.foto} alt="" onClick={()=>window.open(r.foto,"_blank")}
                          style={{width:20,height:20,borderRadius:4,objectFit:"cover",cursor:"pointer",border:"1px solid #e5e7eb"}}/>
                      ) : (
                        <label style={{cursor:"pointer",color:"#9ca3af",display:"flex",fontSize:12}} title="Aggiungi foto a questa voce">
                          📷
                          <input type="file" accept="image/*" capture="environment" style={{display:"none"}}
                            onChange={async e=>{
                              const f = e.target.files[0]; e.target.value="";
                              if (!f) return;
                              try {
                                const { data } = await comprimiImmagine(f);
                                updVoce(gi, r.id, {foto:data});
                              } catch(err) { addToast(err.message||"Errore nel caricamento della foto","error"); }
                            }}/>
                        </label>
                      )}
                    </div>
                  </div>
                  <CellaVoce attivo={r.fatto} colore="#374151" sfondo="#e5e7eb" titolo="Effettuato" disabilitato={!!r.np} onClick={()=>toggleEffettuato(gi,r)}/>
                  <CellaVoce attivo={r.np} colore="#6b7280" sfondo="#f3f4f6" disabilitato simbolo="–"
                    titolo={r.np?"Non programmata su questo impianto (dal capitolato del piano)":"N.P. si imposta dal capitolato del piano, non dal rapportino"}/>
                  <CellaVoce attivo={r.esito==="positivo"} colore="#059669" sfondo="#d1fae5" titolo="Esito positivo" disabilitato={!!r.np} onClick={()=>setEsitoVoce(gi,r,"positivo")}/>
                  <CellaVoce attivo={negativa} colore="#dc2626" sfondo="#fee2e2" titolo="Esito negativo" simbolo="✕" disabilitato={!!r.np} onClick={()=>setEsitoVoce(gi,r,"negativo")}/>
                </div>
              )})}
            </div>
          )})}
        </div>
      )}

      {vociNegative.length>0 && (
        <div style={{border:"1px solid #fecaca",background:"#fef2f2",borderRadius:10,padding:12,marginBottom:14}}>
          <div style={{fontSize:12,fontWeight:800,color:"#b91c1c",marginBottom:2}}>⚠️ Allegato — Esiti negativi e misure correttive</div>
          <div style={{fontSize:10,color:"#9ca3af",marginBottom:10}}>
            Per ogni voce con esito negativo, indica in cosa consiste e quali misure correttive vanno applicate. Nel rapportino stampato compare in una pagina a parte.
          </div>
          {vociNegative.map(({gi,v})=>(
            <div key={v.id} style={{background:"#fff",border:"1px solid #fee2e2",borderRadius:8,padding:10,marginBottom:8}}>
              <div style={{fontSize:12,fontWeight:700,color:"#374151",marginBottom:6}}>{v.testo}</div>
              <label style={{fontSize:10.5,fontWeight:600,color:"#6b7280",display:"block",marginBottom:3}}>In cosa consiste l'esito negativo *</label>
              <textarea value={v.anomalia||""} onChange={e=>updVoce(gi,v.id,{anomalia:e.target.value})} rows={2} style={{...inp,width:"100%",resize:"vertical",marginBottom:6}}
                placeholder="Es. Morsetto M4 allentato con segni di surriscaldamento sulla fase L2"/>
              <label style={{fontSize:10.5,fontWeight:600,color:"#6b7280",display:"block",marginBottom:3}}>Misure correttive da applicare</label>
              <textarea value={v.misure||""} onChange={e=>updVoce(gi,v.id,{misure:e.target.value})} rows={2} style={{...inp,width:"100%",resize:"vertical"}}
                placeholder="Es. Sostituzione del morsetto e nuova verifica termografica entro 30 giorni"/>
            </div>
          ))}
        </div>
      )}

      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:10}}>
        <div>
          <label style={{fontSize:11,fontWeight:600,color:"#6b7280",display:"block",marginBottom:4}}>Esito intervento</label>
          <select value={esito} onChange={e=>setEsito(e.target.value)} style={{...inp,width:"100%"}}>
            <option value="">— Seleziona —</option>
            {ESITI.map(e=><option key={e} value={e}>{e}</option>)}
          </select>
        </div>
        <div>
          <label style={{fontSize:11,fontWeight:600,color:"#6b7280",display:"block",marginBottom:4}}>Ore lavorate</label>
          <input type="number" min="0" step="0.5" value={oreLavorate} onChange={e=>setOreLavorate(e.target.value)} style={{...inp,width:"100%"}}/>
        </div>
      </div>
      <div style={{marginBottom:10}}>
        <label style={{fontSize:11,fontWeight:600,color:"#6b7280",display:"block",marginBottom:4}}>Materiali utilizzati</label>
        <input value={materiali} onChange={e=>setMateriali(e.target.value)} style={{...inp,width:"100%"}} placeholder="Es. N.2 fusibili, guaina isolante..."/>
      </div>
      <div style={{marginBottom:10}}>
        <label style={{fontSize:11,fontWeight:600,color:"#6b7280",display:"block",marginBottom:4}}>Misure rilevate (facoltative)</label>
        {misure.length===0 && (
          <div style={{fontSize:10,color:"#9ca3af",marginBottom:6}}>Es. "Tensione L1" → "231V", "Temperatura inverter" → "42°C"</div>
        )}
        {misure.map(m=>(
          <div key={m.id} style={{display:"flex",gap:6,marginBottom:6}}>
            <input value={m.label} onChange={e=>updMisura(m.id,"label",e.target.value)} style={{...inp,flex:1}} placeholder="Cosa hai misurato"/>
            <input value={m.valore} onChange={e=>updMisura(m.id,"valore",e.target.value)} style={{...inp,width:120}} placeholder="Valore"/>
            <button type="button" onClick={()=>rmMisura(m.id)} style={{background:"none",border:"none",color:"#fca5a5",cursor:"pointer"}}><Trash2 size={14}/></button>
          </div>
        ))}
        <button type="button" onClick={addMisura} style={{fontSize:12,color:"#7c3aed",background:"none",border:"none",cursor:"pointer",fontWeight:600}}>+ Aggiungi misura</button>
      </div>
      <div style={{marginBottom:14}}>
        <label style={{fontSize:11,fontWeight:600,color:"#6b7280",display:"block",marginBottom:4}}>Note intervento</label>
        <textarea value={note} onChange={e=>setNote(e.target.value)} rows={3} style={{...inp,width:"100%",resize:"vertical"}}/>
      </div>

      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:10}}>
        <div>
          <div style={{fontSize:11,fontWeight:600,color:"#6b7280",marginBottom:4}}>Firma Tecnico</div>
          {firmaTecnico ? (
            <div style={{position:"relative"}}>
              <FirmaImmagine src={firmaTecnico} alt="Firma tecnico" style={{maxWidth:"100%",maxHeight:140,objectFit:"contain",display:"block",border:"1px solid #e5e7eb",borderRadius:8,background:"#fff"}}/>
              <button onClick={()=>setFirmaTecnico(null)} style={{position:"absolute",top:4,right:4,background:"#fee2e2",border:"none",borderRadius:6,cursor:"pointer",padding:"2px 6px",fontSize:10,color:"#dc2626"}}>× Rifai</button>
            </div>
          ) : mostraFirmaTec ? (
            <FirmaCanvas onSave={d=>{setFirmaTecnico(d);setMostraFirmaTec(false);}} onCancel={()=>setMostraFirmaTec(false)}/>
          ) : (
            <>
              <button onClick={()=>setMostraFirmaTec(true)} className="btn btn-secondary" style={{width:"100%",fontSize:12}}>✍️ Firma ora</button>
              {/* firma salvata del tecnico (components/MiaFirma.js) */}
              <UsaMiaFirma onUsa={d=>setFirmaTecnico(d)} style={{width:"100%",marginTop:6}} />
            </>
          )}
        </div>
        <div>
          <div style={{fontSize:11,fontWeight:600,color:"#6b7280",marginBottom:4}}>Firma Cliente (facoltativa)</div>
          {firmaCliente ? (
            <div style={{position:"relative"}}>
              <FirmaImmagine src={firmaCliente} alt="Firma cliente" style={{maxWidth:"100%",maxHeight:140,objectFit:"contain",display:"block",border:"1px solid #e5e7eb",borderRadius:8,background:"#fff"}}/>
              <button onClick={()=>setFirmaCliente(null)} style={{position:"absolute",top:4,right:4,background:"#fee2e2",border:"none",borderRadius:6,cursor:"pointer",padding:"2px 6px",fontSize:10,color:"#dc2626"}}>× Rifai</button>
            </div>
          ) : mostraFirmaCli ? (
            <FirmaCanvas onSave={d=>{setFirmaCliente(d);setMostraFirmaCli(false);}} onCancel={()=>setMostraFirmaCli(false)}/>
          ) : (
            <button onClick={()=>setMostraFirmaCli(true)} className="btn btn-secondary" style={{width:"100%",fontSize:12}}>✍️ Firma ora</button>
          )}
        </div>
      </div>

      {mancanti.length>0 && (
        <div style={{background:"#fffbeb",border:"1px solid #fde68a",borderRadius:8,padding:"8px 12px",marginBottom:12,fontSize:11,color:"#92400e"}}>
          ⚠️ Se salvi ora, l'intervento risulterà <strong>"Da completare"</strong> — manca: {mancanti.join(", ")}. Potrai completarlo più tardi.
        </div>
      )}

      <div style={{display:"flex",gap:8}}>
        <button className="btn btn-primary"
          onClick={async ()=>{
            // la bozza si ferma mentre si salva; se il salvataggio non riesce riprende
            bozza.salvato();
            try { await onSalva(costruisciDati()); } catch (e) { bozza.riattiva(); throw e; }
          }}>
          {mancanti.length===0 ? "✅ Salva e completa intervento" : "💾 Salva comunque (Da completare)"}
        </button>
        <button className="btn btn-secondary" onClick={onAnnulla}>Annulla</button>
        {onAnteprima && (
          <button className="btn btn-secondary" style={{marginLeft:"auto"}}
            onClick={()=>onAnteprima({...costruisciDati(), data: esistente?.data||toLocalDateStr(new Date()), tecnicoNome: esistente?.tecnicoNome||""})}>
            🖨️ Anteprima / Stampa PDF
          </button>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
export default function ManutenzioneTab({ projectId, orgId, project, currentUser }) {
  const { addToast } = useToast();
  const { userProfile, isPlatformOwner } = useAuth();

  const [orgInfo, setOrgInfo] = useState(null);
  const [impianti, setImpianti] = useState([]);
  const [impiantoSel, setImpiantoSel] = useState(null);
  const [componenti, setComponenti] = useState([]);
  const [piani, setPiani] = useState([]);
  const [pianoSel, setPianoSel] = useState(null);
  const [interventi, setInterventi] = useState([]);

  const [vistaImpianto, setVistaImpianto] = useState("componenti");
  const [vistaDashboard, setVistaDashboard] = useState(false);
  const [mostraFormImpianto, setMostraFormImpianto] = useState(false);
  // Impianti già censiti in anagrafica per QUESTO cliente: sono quelli che si possono
  // collegare invece di ricrearli. Se la commessa non ha un cliente collegato l'elenco
  // resta vuoto e il form propone direttamente la creazione.
  const [impiantiAnagrafica, setImpiantiAnagrafica] = useState([]);
  useEffect(()=>{
    if (!orgId || !project?.clienteId) { setImpiantiAnagrafica([]); return; }
    return onSnapshot(
      query(collection(db,"impianti"), where("organizationId","==",orgId), where("clienteId","==",project.clienteId)),
      snap => setImpiantiAnagrafica(
        snap.docs.map(d=>({id:d.id,...d.data()}))
          .filter(i=>i.statoImpianto!=="dismesso")
          .sort((a,b)=>(a.denominazione||"").localeCompare(b.denominazione||"","it"))
      ),
      err => { console.error("Errore lettura anagrafica impianti:", err); setImpiantiAnagrafica([]); }
    );
  },[orgId, project?.clienteId]);
  const [formComponenteAperto, setFormComponenteAperto] = useState(null); // {parentId, parentNome} | null
  const [mostraFormPiano, setMostraFormPiano] = useState(false);
  const [modalitaPianoComponenti, setModalitaPianoComponenti] = useState(false);
  const [mostraFormIntervento, setMostraFormIntervento] = useState(false);
  const [mostraRegistraChiamata, setMostraRegistraChiamata] = useState(false);
  const [mostraModificaCapitolato, setMostraModificaCapitolato] = useState(false);
  const [mostraStorico, setMostraStorico] = useState(false);
  const [interventoInModifica, setInterventoInModifica] = useState(null);
  const [riprendiBozzaIntervento, setRiprendiBozzaIntervento] = useState(false);
  // L'intervento genera un rapportino: con le firme vale lo stesso blocco (utils/bloccoRapportini.js)
  const apriCompletamento = (iv) => {
    const v = verificaModificaRapportino(iv, { uid: currentUser?.uid, role: userProfile?.role, ruoloOperativo: userProfile?.ruoloOperativo, isPlatformOwner, commessa: project });
    if (!v.ok) { addToast(v.avviso, "viola", 9000); return; }
    setRiprendiBozzaIntervento(false);
    setInterventoInModifica(iv);
  };
  const [componenteEspanso, setComponenteEspanso] = useState({});
  const [storicoComponente, setStoricoComponente] = useState(null);

  useEffect(() => {
    if (!orgId) return;
    getDoc(doc(db,"organizations",orgId)).then(s=>{ if (s.exists()) setOrgInfo(s.data()); });
  }, [orgId]);

  // Anagrafica completa del cliente (indirizzo, P.IVA, referente) per il rapporto stampato.
  const [clienteInfo, setClienteInfo] = useState(null);
  useEffect(() => {
    if (!project?.clienteId) { setClienteInfo(null); return; }
    getDoc(doc(db,"clienti",project.clienteId))
      .then(s => setClienteInfo(s.exists() ? s.data() : null))
      .catch(() => setClienteInfo(null));
  }, [project?.clienteId]);

  useEffect(() => {
    if (!orgId || !projectId) return;
    return onSnapshot(query(collection(db,"manutenzioneImpianti"), where("organizationId","==",orgId), where("projectId","==",projectId)),
      snap => setImpianti(snap.docs.map(d=>({id:d.id,...d.data()}))));
  }, [orgId, projectId]);

  useEffect(() => {
    if (!orgId || !impiantoSel) { setComponenti([]); return; }
    return onSnapshot(query(collection(db,"manutenzioneComponenti"), where("organizationId","==",orgId), where("impiantoId","==",impiantoSel)),
      snap => setComponenti(snap.docs.map(d=>({id:d.id,...d.data()}))));
  }, [orgId, impiantoSel]);

  useEffect(() => {
    if (!orgId || !impiantoSel) { setPiani([]); return; }
    return onSnapshot(query(collection(db,"manutenzionePiani"), where("organizationId","==",orgId), where("impiantoId","==",impiantoSel)),
      snap => setPiani(snap.docs.map(d=>({id:d.id,...d.data()}))));
  }, [orgId, impiantoSel]);

  useEffect(() => {
    if (!orgId || !pianoSel) { setInterventi([]); return; }
    return onSnapshot(query(collection(db,"manutenzioneInterventi"), where("organizationId","==",orgId), where("pianoId","==",pianoSel)),
      snap => {
        const l = snap.docs.map(d=>({id:d.id,...d.data()}));
        l.sort((a,b)=>(b.data||"").localeCompare(a.data||""));
        setInterventi(l);
      });
  }, [orgId, pianoSel]);

  // Interventi di tutti i piani dell'impianto corrente (serve a Storico componente e Dashboard)
  const [interventiImpianto, setInterventiImpianto] = useState([]);
  useEffect(() => {
    if (!orgId || !impiantoSel) { setInterventiImpianto([]); return; }
    return onSnapshot(query(collection(db,"manutenzioneInterventi"), where("organizationId","==",orgId), where("impiantoId","==",impiantoSel)),
      snap => setInterventiImpianto(snap.docs.map(d=>({id:d.id,...d.data()}))));
  }, [orgId, impiantoSel]);

  const impiantoCorrente = impianti.find(i=>i.id===impiantoSel);

  // Ricavo + verifica SLA di un intervento completato — funzione unica condivisa tra "nuovo
  // intervento" e "completa un intervento lasciato da qualcun altro" (es. segreteria riceve
  // la chiamata e apre il record, un tecnico diverso lo completa più tardi): stessa logica,
  // un solo posto, per non farla divergere tra i due punti come già successo altrove oggi.
  // "dataEvento"/"oraEvento" sono SEMPRE quelli di quando l'intervento è nato davvero (la
  // chiamata, o l'apertura del cantiere) — mai il momento in cui qualcuno chiude la pratica
  // dopo, altrimenti una chiamata di sabato notte completata il lunedì risulterebbe feriale.
  const generaRicavoESla = async (ivId, dati, dataEvento, oraEvento, impiantoNome) => {
    if (dati.stato !== "completato") return;
    try {
      const contrSnap = await getDocs(query(collection(db,"contrattiManutenzione"), where("projectId","==",projectId), where("orgId","==",orgId)));
      if (contrSnap.empty) return;
      const contratto = contrSnap.docs[0].data();
      const fascia = classificaFasciaIntervento(dataEvento, oraEvento, orgInfo||{});
      const tariffa = numIt(contratto[`costo${fascia.charAt(0).toUpperCase()}${fascia.slice(1)}`]); // "45,50" → 45,5 (parseFloat dava 45)
      if (tariffa > 0) {
        const destSeparata = contratto.destinazioneRicavi === "commessaSeparata" && contratto.commessaRicaviId;
        const projectIdRicavo = destSeparata ? contratto.commessaRicaviId : projectId;
        await addDoc(collection(db,"movimentiEconomici"), {
          projectId: projectIdRicavo, organizationId: orgId,
          tipo: "ricavo", categoriaId: "manutenzione", categoriaLabel: "Manutenzione",
          dati: { descrizione: `Intervento ${fascia} — ${impiantoNome||""}`, fascia, interventoId: ivId },
          importo: tariffa,
          creadaDa: currentUser.uid, creadaAt: serverTimestamp(),
        });
        await logModifica({ orgId, uid: currentUser.uid, nomeUtente: currentUser.displayName||"",
          sezione: "manutenzioneInterventi", refId: ivId, refNome: impiantoNome||"",
          campo: "ricavo", campoLabel: "Ricavo intervento generato", before: "—", after: `${fascia} · € ${tariffa}` });
      }

      // Rispetto SLA — solo se questo intervento nasce da una chiamata (campo facoltativo
      // compilato). Il tempo impiegato si misura fino ad ORA (il momento in cui l'intervento
      // viene davvero segnato completato, anche se da una persona diversa in un secondo
      // momento) — è la miglior approssimazione disponibile finché non si registra anche un
      // orario separato di "lavoro concluso sul posto".
      if (dati.richiestaAt) {
        const oreImpiegate = (new Date() - new Date(dati.richiestaAt)) / (1000*60*60);
        const slaOre = parseFloat(contratto[`tempoIntervento${fascia.charAt(0).toUpperCase()}${fascia.slice(1)}`]);
        if (slaOre > 0) {
          const slaRispettato = oreImpiegate <= slaOre;
          await setDoc(doc(db,"manutenzioneInterventi",ivId), { oreImpiegateRisposta: Math.round(oreImpiegate*10)/10, slaOrePromesse: slaOre, slaRispettato }, { merge:true });
          if (!slaRispettato) {
            addToast(`⚠️ SLA non rispettato: promesse ${slaOre}h, impiegate ${Math.round(oreImpiegate*10)/10}h`,"info");
          }
        }
      }
    } catch(err) {
      // Un intervento già salvato non deve mai andare perso per un problema sul ricavo — si
      // registra comunque, il ricavo si può sempre aggiungere a mano dopo dal Controllo
      // Economico della commessa giusta.
      addToast("Intervento salvato, ma il ricavo automatico non è stato generato: "+(err.message||"errore"),"info");
    }
  };

  // Crea un nuovo intervento — condivisa tra il modulo completo ("Nuovo intervento") e il
  // modulo veloce ("Registra chiamata"): stessa identica scrittura su manutenzioneInterventi
  // + Registro Interventi + Smart Overview, per non tenere due copie che rischiano di
  // divergere. "dati" può arrivare completo (checklist, esito, firme...) o minimo (solo
  // richiestaAt + nota) — funziona in entrambi i casi, restando sempre "da completare"
  // finché qualcuno non chiude davvero la checklist.
  const creaIntervento = async (dati) => {
    const dataOggi = toLocalDateStr(new Date());
    const ivRef = await addDoc(collection(db,"manutenzioneInterventi"), {
      organizationId: orgId, pianoId: pianoCorrente.id, impiantoId: impiantoSel, projectId,
      data: dataOggi,
      tecnicoUid: currentUser.uid, tecnicoNome: currentUser.displayName||"",
      ...dati, createdAt: serverTimestamp(),
    });

    // Ricavo + verifica SLA — fascia calcolata su data/ora di creazione di QUESTO
    // intervento (dataOggi/ora), non su "adesso": qui coincidono perché è lo stesso
    // istante, ma la funzione è condivisa con il percorso di completamento successivo,
    // dove invece possono essere momenti diversi.
    const oraCreazione = new Date().toTimeString().slice(0,5);
    await generaRicavoESla(ivRef.id, dati, dataOggi, oraCreazione, impiantoCorrente?.nome);

    // Propagazione a Registro Interventi (riusa "rapportini") + Smart Overview
    const anno = new Date().getFullYear();
    const progressivo = await prossimoProgressivo(`${orgId}_interventi_${anno}`, { orgId, anno });
    const numero = `INT-${anno}-${String(progressivo).padStart(4,"0")}`;

    const rapportinoRef = await addDoc(collection(db,"rapportini"), {
      orgId, projectId, projectName: project?.name||"", codiceCommessa: project?.codiceCommessa||"",
      numero, data: dataOggi,
      // Il cliente si sa già dalla commessa, quindi si precompila da sola. Il sito invece
      // resta da compilare: lo stesso cliente può avere più capannoni/uffici/abitazioni,
      // e solo chi va sul posto sa davvero dove si trova questo impianto specifico.
      nomeCliente: project?.clienteNome||"",
      operatori: [currentUser.displayName||""],
      operatoriUid: [currentUser.uid||""], // le ore contano sui costi solo tramite l'account (W434)
      motivoIntervento: `Manutenzione programmata — ${pianoCorrente.nome}`,
      tipologiaIntervento: "manutenzione",
      descrizioneBreve: `Manutenzione: ${pianoCorrente.nome} (${impiantoCorrente?.nome||""})`,
      lavorazioni: dati.note || "Vedi checklist manutenzione collegata.",
      checklistManutenzione: snapshotChecklist(dati.checklistCompilata),
      haEsitiNegativi: !!dati.haEsitiNegativi,
      sitoOggetto: dati.sito || "",
      esitoIntervento: ESITO_A_STATI_INTERVENTO[dati.esito] || "",
      stato: (dati.firmaTecnico && dati.firmaCliente) ? "firmato" : "bozza",
      firmaTecnico: dati.firmaTecnico || null,
      firmaCliente: dati.firmaCliente || null,
      manutenzioneInterventoId: ivRef.id,
      createdAt: serverTimestamp(),
    });
    await setDoc(doc(db,"manutenzioneInterventi",ivRef.id), { rapportinoId: rapportinoRef.id }, { merge:true });

    await logEvento({
      orgId, tipo:"manutenzione_intervento", categoria:"rapportini",
      titolo: `Manutenzione ${dati.stato==="completato"?"completata":"da completare"} — ${pianoCorrente.nome}`,
      uid: currentUser.uid, nomeUtente: currentUser.displayName||"",
      refId: ivRef.id, refTipo:"manutenzione", refNome: pianoCorrente.nome,
      clienteId: project?.clienteId||"", clienteNome: project?.clienteNome||"",
      projectId, projectName: project?.name||"",
      priorita: dati.stato==="completato" ? "info" : "medio",
    });
    return ivRef;
  };
  const pianoCorrente = piani.find(p=>p.id===pianoSel);

  // ── Dashboard Manutenzione (sull'intera commessa) ───────────────────────
  if (vistaDashboard) {
    const oggiStr = toLocalDateStr(new Date());
    const tra30 = new Date(); tra30.setDate(tra30.getDate()+30);

    // Per la dashboard servono TUTTI i piani/interventi della commessa, non solo dell'impianto aperto.
    // Usiamo i dati già caricati per l'impianto corrente, più un caricamento leggero degli altri impianti.
    return (
      <DashboardManutenzione orgId={orgId} projectId={projectId} project={project}
        onVaiAlPiano={(imId,pId)=>{ setImpiantoSel(imId); setPianoSel(pId); setVistaDashboard(false); }}
        onChiudi={()=>setVistaDashboard(false)}/>
    );
  }

  // ── Livello 3: Interventi di un piano ───────────────────────────────────
  if (pianoCorrente) {
    const anagImp = impiantiAnagrafica.find(a => a.id === impiantoCorrente?.impiantoAnagraficaId);
    const sitoPredef = [anagImp?.sede?.nome, anagImp?.ubicazione, anagImp?.indirizzo].filter(Boolean).join(" — ");
    // Modulo vuoto da stampare e compilare a mano sul posto: stesse voci, cadenze e quattro
    // colonne del rapportino digitale, con le caselle da barrare e la pagina allegata per gli
    // esiti negativi e le misure correttive.
    const stampaModuloVuoto = () => {
      const win = window.open("","_blank");
      const gruppiV = costruisciGruppiChecklist(componenti, pianoCorrente);
      const logoHtml = orgInfo?.logo ? `<img src="${orgInfo.logo}" style="height:36px;object-fit:contain;"/>` :
        `<div style="font-size:16px;font-weight:900;color:#1e1b4b;">◆ ${orgInfo?.nome||orgInfo?.name||""}</div>`;
      const ci = clienteInfo || {};
      const nomeCliente = ci.ragioneSociale || project?.clienteNome || "—";
      const datiCliente = [ci.indirizzo, ci.piva ? `P.IVA ${ci.piva}` : (ci.codiceFiscale ? `C.F. ${ci.codiceFiscale}` : ""), ci.referente ? `Ref. ${ci.referente}` : "", ci.telefono ? `Tel. ${ci.telefono}` : ""].filter(Boolean);
      const datiAzienda = [orgInfo?.indirizzo, orgInfo?.piva ? `P.IVA ${orgInfo.piva}` : "", orgInfo?.telefono ? `Tel. ${orgInfo.telefono}` : "", orgInfo?.email].filter(Boolean).join(" · ");
      const bx = '<span class="bx"></span>';
      const conCad = gruppiV.some(g=>g.voci.some(v=>v.frequenzaMesi));
      const th = 'font-weight:700;background:#f3f4f6;text-align:center;font-size:8.5px;';
      const tabelle = gruppiV.map(g => `
        ${g.componenteNome ? `<div style="font-size:11px;font-weight:700;color:#5b21b6;margin:8px 0 2px;">${g.componenteNome}</div>` : ""}
        <table><thead><tr><td style="font-weight:700;background:#f3f4f6;font-size:9px;">Voce</td>${conCad?`<td style="${th}width:58px">Cadenza</td><td style="${th}width:62px">Prossima scad.</td>`:""}<td style="${th}width:44px">Effettuato</td><td style="${th}width:34px">N.P.</td><td style="${th}width:44px">Esito positivo</td><td style="${th}width:44px">Esito negativo</td></tr></thead>
        <tbody>${g.voci.map(v => {
          if (v.np) {
            const bxNo = '<span class="bx" style="border-color:#d1d5db;"></span>';
            const bxNP = '<span class="bx" style="text-align:center;line-height:11px;font-weight:900;font-size:12px;">&#10004;</span>';
            return `<tr class="rg"><td style="color:#6b7280">${v.testo}</td>${conCad?`<td style="font-size:8.5px;color:#9ca3af;text-align:center">—</td><td style="font-size:8.5px;color:#9ca3af;text-align:center">non progr.</td>`:""}<td class="c">${bxNo}</td><td class="c">${bxNP}</td><td class="c">${bxNo}</td><td class="c">${bxNo}</td></tr>`;
          }
          const dProssima = v.frequenzaMesi ? prossimaScadenzaVoce(v, pianoCorrente, interventi) : null;
          const prossima = dProssima ? dProssima.toLocaleDateString("it-IT") : "";
          return `<tr class="rg"><td>${v.testo}</td>${conCad?`<td style="font-size:8.5px;color:#6b7280;text-align:center">${v.frequenzaMesi?(FREQUENZE_VOCE[v.frequenzaMesi]||v.frequenzaMesi+" mesi"):""}</td><td style="font-size:8.5px;color:#6b7280;text-align:center">${prossima}</td>`:""}<td class="c">${bx}</td><td class="c">${bx}</td><td class="c">${bx}</td><td class="c">${bx}</td></tr>`;
        }).join("")}</tbody></table>`).join("");
      const righeAllegato = Array.from({length:8}).map(() => '<tr class="al"><td></td><td></td><td></td></tr>').join("");
      win.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"/>
      <title>Modulo rapporto manutenzione</title>
      <style>*{margin:0;padding:0;box-sizing:border-box;}body{font-family:Arial,sans-serif;color:#111;padding:12mm 14mm;}
      @media screen{body{max-width:800px;margin:0 auto;background:#fff;box-shadow:0 2px 16px rgba(0,0,0,.08);}}
      @page{size:A4 portrait;margin:8mm;}
      .hdr{border-bottom:3px solid #1e1b4b;padding-bottom:8px;margin-bottom:10px;display:flex;justify-content:space-between;}
      table{width:100%;border-collapse:collapse;margin-bottom:8px;}td{padding:3px 7px;border:1px solid #9ca3af;font-size:10.5px;}
      thead{display:table-header-group;} tr{page-break-inside:avoid;}
      tr.rg td{height:24px;} td.c{text-align:center;} .bx{display:inline-block;width:14px;height:14px;border:1.5px solid #374151;border-radius:2px;vertical-align:middle;}
      tr.al td{height:52px;} .box{border:1px solid #9ca3af;border-radius:5px;padding:6px 9px;font-size:10.5px;line-height:1.5;}
      .box h4{font-size:8px;letter-spacing:.7px;text-transform:uppercase;color:#6b7280;margin-bottom:2px;}
      .ln{border-bottom:1px solid #9ca3af;height:20px;} .sign{width:45%;display:inline-block;}
      @media print{.noprint{display:none!important;}body{padding:0;box-shadow:none!important;}}</style></head><body>
      <div class="noprint" style="text-align:right;margin-bottom:10px;">
        <button onclick="window.print()" style="background:#7c3aed;color:#fff;border:none;border-radius:8px;padding:9px 18px;font-size:13px;font-weight:700;cursor:pointer;">🖨️ Stampa / Salva come PDF</button>
      </div>
      <div class="hdr"><div>${logoHtml}<div style="font-size:10px;color:#6b7280;margin-top:3px;">${orgInfo?.nome||orgInfo?.name||""}${datiAzienda?`<br/>${datiAzienda}`:""}</div></div>
        <div style="text-align:right"><div style="font-size:15px;font-weight:800;color:#1e1b4b">RAPPORTO MANUTENZIONE</div>
        <div style="font-size:11px;color:#6b7280">Data ____ / ____ / ________</div></div></div>
      <div style="display:flex;gap:10px;margin-bottom:8px;">
        <div class="box" style="flex:1;"><h4>Cliente</h4><strong style="font-size:12px;">${nomeCliente}</strong>${datiCliente.length?`<br/>${datiCliente.join("<br/>")}`:""}</div>
        <div class="box" style="flex:1;"><h4>Sito dell'intervento</h4><strong style="font-size:12px;">${sitoPredef||""}</strong><div class="ln"></div>Impianto: ${impiantoCorrente?.nome||"—"}${anagImp?.matricola?` · Matr. ${anagImp.matricola}`:""}</div>
      </div>
      <table><tr><td style="font-weight:700;width:14%">Commessa</td><td colspan="3">${project?.codiceCommessa?`[${project.codiceCommessa}] `:""}${project?.name||""}</td></tr>
      <tr><td style="font-weight:700">Piano</td><td colspan="3">${pianoCorrente.nome}</td></tr>
      <tr><td style="font-weight:700">Tecnico</td><td style="width:36%"></td><td style="font-weight:700;width:14%">Ore lavorate</td><td></td></tr></table>
      <div style="font-size:9px;color:#6b7280;margin-bottom:4px;">Barrare per ogni voce: <b>Effettuato</b>, oppure <b>N.P.</b> (non previsto); poi l'esito <b>positivo</b> o <b>negativo</b>. Per ogni esito negativo compilare l'Allegato A.</div>
      ${tabelle}
      <div style="font-size:11px;margin:8px 0 3px;"><strong>Materiali utilizzati:</strong></div><div class="ln"></div><div class="ln"></div>
      <div style="font-size:11px;margin:8px 0 3px;"><strong>Note:</strong></div><div class="ln"></div><div class="ln"></div><div class="ln"></div>
      <div style="font-size:11px;margin:10px 0;"><strong>Esito intervento:</strong> &nbsp; ${bx} Risolto &nbsp;&nbsp; ${bx} Parziale &nbsp;&nbsp; ${bx} Non risolto &nbsp;&nbsp; ${bx} Da riprogrammare</div>
      <div style="margin-top:14px;">
        <div class="sign">Firma Tecnico<div style="height:52px;border-bottom:1px solid #9ca3af;"></div></div>
        <div class="sign" style="margin-left:5%;">Firma Cliente<div style="height:52px;border-bottom:1px solid #9ca3af;"></div></div>
      </div>
      <div style="page-break-before:always;"></div>
      <div class="hdr"><div>${logoHtml}</div><div style="text-align:right"><div style="font-size:15px;font-weight:800;color:#b91c1c">ALLEGATO A — ESITI NEGATIVI E MISURE CORRETTIVE</div>
        <div style="font-size:11px;color:#6b7280">${project?.codiceCommessa?`[${project.codiceCommessa}] `:""}${project?.name||""} · ${pianoCorrente.nome}</div></div></div>
      <table><tr><td style="font-weight:700;background:#fef2f2;width:26%">Voce</td><td style="font-weight:700;background:#fef2f2;width:37%">In cosa consiste l'esito negativo</td><td style="font-weight:700;background:#fef2f2;width:37%">Misure correttive da applicare</td></tr>${righeAllegato}</table>
      <div style="margin-top:14px;">
        <div class="sign">Firma Tecnico<div style="height:48px;border-bottom:1px solid #9ca3af;"></div></div>
        <div class="sign" style="margin-left:5%;">Firma Cliente per presa visione<div style="height:48px;border-bottom:1px solid #9ca3af;"></div></div>
      </div>
      </body></html>`);
      win.document.close();
    };

    const stampaPDF = (interv) => {
      const win = window.open("","_blank");
      const logoHtml = orgInfo?.logo ? `<img src="${orgInfo.logo}" style="height:36px;object-fit:contain;"/>` :
        `<div style="font-size:16px;font-weight:900;color:#1e1b4b;">◆ ${orgInfo?.nome||orgInfo?.name||""}</div>`;
      const ci = clienteInfo || {};
      const nomeCliente = ci.ragioneSociale || project?.clienteNome || "—";
      const datiCliente = [ci.indirizzo, ci.piva ? `P.IVA ${ci.piva}` : (ci.codiceFiscale ? `C.F. ${ci.codiceFiscale}` : ""), ci.referente ? `Ref. ${ci.referente}` : "", ci.telefono ? `Tel. ${ci.telefono}` : "", ci.email].filter(Boolean);
      const datiAzienda = [orgInfo?.indirizzo, orgInfo?.piva ? `P.IVA ${orgInfo.piva}` : "", orgInfo?.telefono ? `Tel. ${orgInfo.telefono}` : "", orgInfo?.email].filter(Boolean).join(" · ");
      const sitoStampa = interv.sito || sitoPredef || "";
      win.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"/>
      <title>Rapporto Manutenzione</title>
      <style>*{margin:0;padding:0;box-sizing:border-box;}body{font-family:Arial,sans-serif;color:#111;padding:14mm 16mm;}
      @media screen{body{max-width:800px;margin:0 auto;background:#fff;box-shadow:0 2px 16px rgba(0,0,0,.08);}}
      .hdr{border-bottom:3px solid #1e1b4b;padding-bottom:8px;margin-bottom:14px;display:flex;justify-content:space-between;}
      table{width:100%;border-collapse:collapse;margin-bottom:10px;}td{padding:4px 8px;border:1px solid #e5e7eb;font-size:11px;}
      .sign{width:45%;display:inline-block;}
      .box{border:1px solid #e5e7eb;border-radius:6px;padding:7px 10px;font-size:11px;line-height:1.55;}
      .box h4{font-size:8.5px;letter-spacing:.7px;text-transform:uppercase;color:#6b7280;margin-bottom:3px;}
      @media print{.noprint{display:none!important;}body{padding:0;box-shadow:none!important;}}</style></head><body>
      <div class="noprint" style="text-align:right;margin-bottom:10px;">
        <button onclick="window.print()" style="background:#7c3aed;color:#fff;border:none;border-radius:8px;padding:9px 18px;font-size:13px;font-weight:700;cursor:pointer;">🖨️ Stampa / Salva come PDF</button>
      </div>
      <div class="hdr"><div>${logoHtml}<div style="font-size:10px;color:#6b7280;margin-top:3px;">${orgInfo?.nome||orgInfo?.name||""}${datiAzienda?`<br/>${datiAzienda}`:""}</div></div>
        <div style="text-align:right"><div style="font-size:15px;font-weight:800;color:#1e1b4b">RAPPORTO MANUTENZIONE</div>
        <div style="font-size:11px;color:#6b7280">del ${interv.data.split("-").reverse().join("/")}</div></div></div>
      <div style="display:flex;gap:10px;margin-bottom:10px;">
        <div class="box" style="flex:1;"><h4>Cliente</h4><strong style="font-size:12px;">${nomeCliente}</strong>${datiCliente.length?`<br/>${datiCliente.join("<br/>")}`:""}</div>
        <div class="box" style="flex:1;"><h4>Sito dell'intervento</h4><strong style="font-size:12px;">${sitoStampa||"—"}</strong><br/>Impianto: ${impiantoCorrente?.nome||"—"}${anagImp?.tipologia?` (${anagImp.tipologia})`:""}${anagImp?.matricola?`<br/>Matricola: ${anagImp.matricola}`:""}${anagImp?.costruttore?` · ${anagImp.costruttore}`:""}</div>
      </div>
      <table><tr><td style="font-weight:700">Commessa</td><td colspan="3">${project?.codiceCommessa?`[${project.codiceCommessa}] `:""}${project?.name||""}</td></tr>
      <tr><td style="font-weight:700">Piano</td><td colspan="3">${pianoCorrente.nome}</td></tr>
      <tr><td style="font-weight:700">Tecnico</td><td>${interv.tecnicoNome||""}</td><td style="font-weight:700">Esito</td><td>${interv.esito||"—"}</td></tr>
      <tr><td style="font-weight:700">Ore lavorate</td><td>${interv.oreLavorate||"—"}</td><td style="font-weight:700">Stato</td><td>${interv.stato==="completato"?"Completato":"Da completare"}</td></tr></table>
      ${(interv.checklistCompilata||[]).length ? `<div style="font-size:12px;font-weight:700;margin-bottom:6px;">${interv.checklistCompilata.some(g=>g.voci.some(v=>v.frequenzaMesi))?"Voci del capitolato":"Checklist"}</div>${interv.checklistCompilata.map(g=>{
        const conCad = g.voci.some(v=>v.frequenzaMesi);
        const th = 'font-weight:700;background:#f3f4f6;text-align:center;font-size:9px;';
        const ck = (on,col) => on ? `<span style="color:${col};font-weight:900;font-size:13px;">&#10004;</span>` : "";
        return `
        ${g.componenteNome ? `<div style="font-size:11px;font-weight:700;color:#5b21b6;margin:8px 0 2px;">${g.componenteNome}</div>` : ""}
        <table><tr><td style="font-weight:700;background:#f3f4f6;font-size:9px;">Voce</td>${conCad?`<td style="${th}width:62px">Cadenza</td><td style="${th}width:66px">Prossima scad.</td>`:""}<td style="${th}width:44px">Effettuato</td><td style="${th}width:34px">N.P.</td><td style="${th}width:44px">Esito positivo</td><td style="${th}width:44px">Esito negativo</td><td style="${th}width:40px">Foto</td></tr>
        ${g.voci.map(v=>{
          const dProssima = v.frequenzaMesi ? prossimaScadenzaVoce(v, pianoCorrente, interventi) : null;
          const prossima = dProssima ? dProssima.toLocaleDateString("it-IT") : (v.frequenzaMesi ? "—" : "");
          return `<tr><td${v.np?' style="color:#9ca3af"':""}>${v.testo}</td>${conCad?`<td style="font-size:9px;color:#6b7280;text-align:center">${v.frequenzaMesi?(FREQUENZE_VOCE[v.frequenzaMesi]||v.frequenzaMesi+" mesi"):""}</td><td style="font-size:9px;color:#6b7280;text-align:center">${prossima}</td>`:""}<td style="text-align:center">${ck(v.fatto,"#374151")}</td><td style="text-align:center">${ck(v.np,"#6b7280")}</td><td style="text-align:center">${ck(v.esito==="positivo","#059669")}</td><td style="text-align:center">${ck(v.esito==="negativo","#dc2626")}</td><td style="text-align:center">${v.foto?`<img src="${v.foto}" style="width:30px;height:30px;object-fit:cover;border-radius:4px;"/>`:""}</td></tr>`;
        }).join("")}</table>
      `}).join("")}` : ""}
      ${interv.materiali ? `<div style="font-size:11px;margin-bottom:8px;"><strong>Materiali:</strong> ${interv.materiali}</div>` : ""}
      ${(interv.misure||[]).length ? `<div style="font-size:12px;font-weight:700;margin-bottom:4px;">Misure rilevate</div><table>${interv.misure.map(m=>`<tr><td style="font-weight:700;width:160px">${m.label||"—"}</td><td>${m.valore||"—"}</td></tr>`).join("")}</table>` : ""}
      ${interv.note ? `<div style="font-size:11px;margin-bottom:12px;"><strong>Note:</strong> ${interv.note}</div>` : ""}
      <div style="margin-top:20px;">
        <div class="sign">Firma Tecnico<br/>${interv.firmaTecnico?`<img src="${interv.firmaTecnico}" style="max-height:60px;max-width:100%;object-fit:contain;"/>`:'<div style="height:60px;"></div>'}</div>
        <div class="sign" style="margin-left:5%;">Firma Cliente<br/>${interv.firmaCliente?`<img src="${interv.firmaCliente}" style="max-height:60px;max-width:100%;object-fit:contain;"/>`:'<div style="height:60px;"></div>'}</div>
      </div>
      ${(() => {
        const negativi = (interv.checklistCompilata||[]).flatMap(g=>g.voci.filter(v=>v.esito==="negativo"));
        if (!negativi.length) return "";
        return `<div style="page-break-before:always;"></div>
        <div class="hdr"><div>${logoHtml}</div><div style="text-align:right"><div style="font-size:15px;font-weight:800;color:#b91c1c">ALLEGATO A — ESITI NEGATIVI E MISURE CORRETTIVE</div>
          <div style="font-size:11px;color:#6b7280">${interv.data.split("-").reverse().join("/")} · ${project?.codiceCommessa?`[${project.codiceCommessa}] `:""}${project?.name||""} · ${pianoCorrente.nome}</div></div></div>
        <table><tr><td style="font-weight:700;background:#fef2f2;width:26%">Voce</td><td style="font-weight:700;background:#fef2f2;width:37%">In cosa consiste l'esito negativo</td><td style="font-weight:700;background:#fef2f2;width:37%">Misure correttive da applicare</td></tr>
        ${negativi.map(v=>`<tr><td style="vertical-align:top;font-weight:700">${v.testo}</td><td style="vertical-align:top;white-space:pre-wrap">${(v.anomalia||"—")}${v.foto?`<br/><img src="${v.foto}" style="max-width:150px;margin-top:6px;border-radius:4px;"/>`:""}</td><td style="vertical-align:top;white-space:pre-wrap">${v.misure||"—"}</td></tr>`).join("")}</table>
        <div style="margin-top:24px;font-size:10px;color:#6b7280;">Documento parte integrante del Rapporto Manutenzione del ${interv.data.split("-").reverse().join("/")}.</div>
        <div style="margin-top:14px;"><div class="sign">Firma Tecnico<br/>${interv.firmaTecnico?`<img src="${interv.firmaTecnico}" style="max-height:50px;max-width:100%;object-fit:contain;"/>`:'<div style="height:50px;"></div>'}</div>
        <div class="sign" style="margin-left:5%;">Firma Cliente per presa visione<br/>${interv.firmaCliente?`<img src="${interv.firmaCliente}" style="max-height:50px;max-width:100%;object-fit:contain;"/>`:'<div style="height:50px;"></div>'}</div></div>`;
      })()}
      </body></html>`);
      win.document.close();
    setTimeout(()=>{
      if (win.closed) return;
      const style = win.document.createElement("style");
      style.textContent = "@media print { .no-print-btn { display:none !important; } }";
      win.document.head.appendChild(style);
      const btn = win.document.createElement("button");
      btn.textContent = "🖨️ Stampa";
      btn.className = "no-print-btn";
      btn.onclick = () => win.print();
      btn.style.cssText = "position:fixed;top:16px;right:16px;z-index:9999;padding:10px 20px;background:#7c3aed;color:#fff;border:none;border-radius:8px;font-size:14px;font-weight:700;cursor:pointer;font-family:Arial,sans-serif;box-shadow:0 4px 12px rgba(0,0,0,.2);";
      win.document.body.appendChild(btn);
    }, 50);
    };

    return (
      <div style={{display:"flex",flexDirection:"column",height:"100%",overflow:"hidden"}}>
        <div style={{background:"#fff",borderBottom:"1px solid #e5e7eb",padding:"14px 24px",flexShrink:0}}>
          <button onClick={()=>setPianoSel(null)} style={{background:"none",border:"none",cursor:"pointer",color:"#7c3aed",fontSize:12,fontWeight:600,display:"flex",alignItems:"center",gap:4,marginBottom:6}}>
            <ChevronLeft size={14}/> Torna ai piani
          </button>
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between"}}>
            <div>
              <h2 style={{fontSize:16,fontWeight:700}}>🔧 {pianoCorrente.nome}</h2>
              <p style={{fontSize:12,color:"#6b7280"}}>{impiantoCorrente?.nome}</p>
            </div>
            {pianoCorrente.modalita==="capitolato" && (
              <div style={{display:"flex",gap:8}}>
                <button onClick={()=>setMostraStorico(true)} style={{fontSize:11,color:"#374151",background:"#f3f4f6",border:"none",borderRadius:7,padding:"6px 12px",cursor:"pointer",fontWeight:600}}>
                  📊 Storico
                </button>
                <button onClick={()=>setMostraModificaCapitolato(true)} style={{fontSize:11,color:"#7c3aed",background:"#ede9fe",border:"none",borderRadius:7,padding:"6px 12px",cursor:"pointer",fontWeight:600}}>
                  ✏️ Modifica capitolato
                </button>
              </div>
            )}
          </div>
        </div>
        {mostraModificaCapitolato && (
          <ModificaCapitolato piano={pianoCorrente} onAnnulla={()=>setMostraModificaCapitolato(false)}
            onSalva={async (voci)=>{
              await setDoc(doc(db,"manutenzionePiani",pianoCorrente.id), { voci, updatedAt:serverTimestamp() }, { merge:true });
              addToast("Capitolato aggiornato ✅","success");
              setMostraModificaCapitolato(false);
            }}/>
        )}
        {mostraStorico && (
          <StoricoCapitolato piano={pianoCorrente} interventiPiano={interventi} onChiudi={()=>setMostraStorico(false)}/>
        )}
        <div style={{flex:1,overflowY:"auto",padding:20}}>
          {!mostraFormIntervento && !interventoInModifica && !mostraRegistraChiamata && !mostraModificaCapitolato && !mostraStorico && (
            <RapportinoPiano gruppi={costruisciGruppiChecklist(componenti, pianoCorrente)} piano={pianoCorrente} interventiPiano={interventi} onStampaVuoto={stampaModuloVuoto}/>
          )}
          {/* interventi iniziati e non salvati su questo dispositivo (utils/bozzeLocali.js) */}
          {!mostraFormIntervento && !interventoInModifica && !mostraRegistraChiamata && !mostraModificaCapitolato && !mostraStorico && pianoCorrente && (
            <ElencoBozze prefisso={`${prefissoBozze(currentUser?.uid, "manutenzione", projectId)}${pianoCorrente.id}~`} documenti={interventi}
              idDi={b => documentoBozza(b.chiave).split("~")[1] || "nuovo"}
              testoDi={(b, iv) => iv ? `Modifiche non salvate all'intervento${delGiorno(iv.data)}` : `Intervento non salvato${delGiorno(b.info?.data)}`}
              onRiprendi={iv => {
                if (iv) { apriCompletamento(iv); setRiprendiBozzaIntervento(true); }
                else { setRiprendiBozzaIntervento(true); setMostraFormIntervento(true); }
              }} />
          )}
          {!mostraFormIntervento && !interventoInModifica && !mostraRegistraChiamata && !mostraModificaCapitolato && !mostraStorico && (
            <div style={{display:"flex",gap:8,marginBottom:16}}>
              <button className="btn btn-primary" onClick={()=>{ setRiprendiBozzaIntervento(false); setMostraFormIntervento(true); }} style={{display:"flex",alignItems:"center",gap:6}}>
                <Plus size={14}/> Nuovo intervento
              </button>
              <button className="btn btn-secondary" onClick={()=>setMostraRegistraChiamata(true)} style={{display:"flex",alignItems:"center",gap:6}}>
                📞 Registra chiamata
              </button>
            </div>
          )}
          {mostraRegistraChiamata && (
            <FormRegistraChiamata onAnnulla={()=>setMostraRegistraChiamata(false)}
              onSalva={async ({richiestaAt, note})=>{
                await creaIntervento({
                  checklistCompilata: costruisciGruppiChecklist(componenti, pianoCorrente).map(g=>({...g,voci:g.voci.map(v=>({...v,fatto:false}))})),
                  note, esito:"", oreLavorate:"", materiali:"", misure:[], firmaTecnico:null, firmaCliente:null,
                  stato:"da_completare", mancanti:["Firma Tecnico","Checklist incompleta","Esito intervento"], richiestaAt,
                });
                addToast("Chiamata registrata — un tecnico potrà completare l'intervento più tardi 📞","success");
                setMostraRegistraChiamata(false);
              }}/>
          )}
          {mostraFormIntervento && (
            <FormIntervento piano={pianoCorrente} componenti={componenti} interventiPiano={interventi} sitoPredefinito={sitoPredef} logoAssente={!orgInfo?.logo} onAnteprima={(d)=>stampaPDF({tecnicoNome: currentUser?.displayName||"", ...d})}
              riprendiBozza={riprendiBozzaIntervento} projectId={projectId}
              onAnnulla={()=>setMostraFormIntervento(false)}
              onSalva={async (dati)=>{
                await creaIntervento(dati);
                addToast(dati.stato==="completato" ? "Intervento completato ✅" : "Intervento salvato — Da completare ⚠️", dati.stato==="completato"?"success":"warning");
                setMostraFormIntervento(false);
              }}/>
          )}

          {interventoInModifica && (
            <FormIntervento piano={pianoCorrente} componenti={componenti} interventiPiano={interventi} sitoPredefinito={sitoPredef} logoAssente={!orgInfo?.logo} onAnteprima={(d)=>stampaPDF({tecnicoNome: currentUser?.displayName||"", ...d})} esistente={interventoInModifica}
              riprendiBozza={riprendiBozzaIntervento} projectId={projectId}
              onAnnulla={()=>setInterventoInModifica(null)}
              onSalva={async (dati)=>{
                await setDoc(doc(db,"manutenzioneInterventi",interventoInModifica.id), { ...dati, updatedAt: serverTimestamp() }, { merge:true });

                // Ricavo + verifica SLA — fascia calcolata su data/ora di quando l'intervento
                // è nato DAVVERO (interventoInModifica), non su adesso: chi completa la
                // pratica può essere una persona diversa, in un momento diverso, da chi ha
                // ricevuto la chiamata originale.
                const oraEvento = interventoInModifica.createdAt?.toDate ? interventoInModifica.createdAt.toDate().toTimeString().slice(0,5) : new Date().toTimeString().slice(0,5);
                await generaRicavoESla(interventoInModifica.id, dati, interventoInModifica.data, oraEvento, impiantoCorrente?.nome);

                if (interventoInModifica.rapportinoId) {
                  await setDoc(doc(db,"rapportini",interventoInModifica.rapportinoId), {
                    lavorazioni: dati.note || "Vedi checklist manutenzione collegata.",
                    checklistManutenzione: snapshotChecklist(dati.checklistCompilata),
                    haEsitiNegativi: !!dati.haEsitiNegativi,
                    sitoOggetto: dati.sito || "",
                    esitoIntervento: ESITO_A_STATI_INTERVENTO[dati.esito] || "",
                    stato: (dati.firmaTecnico && dati.firmaCliente) ? "firmato" : "bozza",
                    firmaTecnico: dati.firmaTecnico || null,
                    firmaCliente: dati.firmaCliente || null,
                  }, { merge:true });
                }

                if (dati.stato==="completato" && interventoInModifica.stato!=="completato") {
                  await logEvento({
                    orgId, tipo:"manutenzione_intervento", categoria:"rapportini",
                    titolo: `Manutenzione completata — ${pianoCorrente.nome}`,
                    uid: currentUser.uid, nomeUtente: currentUser.displayName||"",
                    refId: interventoInModifica.id, refTipo:"manutenzione", refNome: pianoCorrente.nome,
                    clienteId: project?.clienteId||"", clienteNome: project?.clienteNome||"",
                    projectId, projectName: project?.name||"",
                    priorita: "info",
                  });
                }

                addToast(dati.stato==="completato" ? "Intervento completato ✅" : "Intervento aggiornato — ancora Da completare ⚠️", dati.stato==="completato"?"success":"warning");
                setInterventoInModifica(null);
              }}/>
          )}

          {interventi.length===0 && !mostraFormIntervento ? (
            <div style={{textAlign:"center",padding:40,color:"#9ca3af"}}>Nessun intervento ancora registrato per questo piano.</div>
          ) : interventi.map(iv=>(
            <div key={iv.id} style={{background:"#fff",border:"1px solid #e5e7eb",borderRadius:10,padding:14,marginBottom:10,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
              <div>
                <div style={{display:"flex",alignItems:"center",gap:8}}>
                  <span style={{fontSize:13,fontWeight:700}}>{iv.data.split("-").reverse().join("/")}</span>
                  <span style={{fontSize:10,fontWeight:700,padding:"2px 8px",borderRadius:10,
                    background:iv.stato==="completato"?"#d1fae5":"#fef3c7",color:iv.stato==="completato"?"#065f46":"#92400e"}}>
                    {iv.stato==="completato"?"✅ Completato":"⚠️ Da completare"}
                  </span>
                  {iv.slaRispettato===true && <span style={{fontSize:10,fontWeight:700,padding:"2px 8px",borderRadius:10,background:"#dbeafe",color:"#1e40af"}}>⏱️ SLA rispettato ({iv.oreImpiegateRisposta}h)</span>}
                  {iv.slaRispettato===false && <span style={{fontSize:10,fontWeight:700,padding:"2px 8px",borderRadius:10,background:"#fee2e2",color:"#991b1b"}}>⏱️ SLA non rispettato ({iv.oreImpiegateRisposta}h su {iv.slaOrePromesse}h)</span>}
                </div>
                <div style={{fontSize:12,color:"#6b7280",marginTop:2}}>Tecnico: {iv.tecnicoNome||"—"} · Esito: {iv.esito||"—"}</div>
              </div>
              <div style={{display:"flex",gap:8}}>
                {iv.stato!=="completato" && !interventoInModifica && !mostraFormIntervento && (
                  <button onClick={()=>apriCompletamento(iv)}
                    style={{background:"#fef3c7",border:"1px solid #fde68a",borderRadius:8,padding:"6px 12px",cursor:"pointer",fontSize:12,fontWeight:700,color:"#92400e"}}>
                    📝 Completa
                  </button>
                )}
                <button onClick={()=>stampaPDF(iv)} style={{background:"none",border:"1px solid #e5e7eb",borderRadius:8,padding:"6px 12px",cursor:"pointer",fontSize:12,display:"flex",alignItems:"center",gap:5}}>
                  <Printer size={13}/> PDF
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // ── Livello 2: Impianto — Componenti + Piani ────────────────────────────
  if (impiantoCorrente) {
    const componentiRadice = componenti.filter(c=>!c.parentId);

    const eliminaComponente = async (comp) => {
      const figli = componenti.filter(c=>c.parentId===comp.id);
      if (figli.length>0) { addToast("Elimina prima i sotto-elementi di questo componente","error"); return; }
      if (!(await confirm(`Eliminare "${comp.nome}"?`, {type:"danger"}))) return;
      await deleteDoc(doc(db,"manutenzioneComponenti",comp.id));
    };

    return (
      <div style={{display:"flex",flexDirection:"column",height:"100%",overflow:"hidden"}}>
        <div style={{background:"#fff",borderBottom:"1px solid #e5e7eb",padding:"14px 24px",flexShrink:0}}>
          <button onClick={()=>setImpiantoSel(null)} style={{background:"none",border:"none",cursor:"pointer",color:"#7c3aed",fontSize:12,fontWeight:600,display:"flex",alignItems:"center",gap:4,marginBottom:6}}>
            <ChevronLeft size={14}/> Torna agli impianti
          </button>
          <h2 style={{fontSize:16,fontWeight:700}}>⚙️ {impiantoCorrente.nome}</h2>
          <div style={{display:"flex",gap:6,marginTop:10}}>
            <button onClick={()=>setVistaImpianto("componenti")}
              style={{padding:"6px 14px",border:"none",borderBottom:`2px solid ${vistaImpianto==="componenti"?"#7c3aed":"transparent"}`,background:"none",color:vistaImpianto==="componenti"?"#7c3aed":"#6b7280",fontSize:13,fontWeight:vistaImpianto==="componenti"?700:500,cursor:"pointer"}}>
              🌳 Componenti
            </button>
            <button onClick={()=>setVistaImpianto("piani")}
              style={{padding:"6px 14px",border:"none",borderBottom:`2px solid ${vistaImpianto==="piani"?"#7c3aed":"transparent"}`,background:"none",color:vistaImpianto==="piani"?"#7c3aed":"#6b7280",fontSize:13,fontWeight:vistaImpianto==="piani"?700:500,cursor:"pointer"}}>
              🛠️ Piani di Manutenzione
            </button>
          </div>
        </div>

        <div style={{flex:1,overflowY:"auto",padding:20}}>
          {/* interventi iniziati e non salvati su questo dispositivo: si apre il loro piano */}
          <ElencoBozze prefisso={prefissoBozze(currentUser?.uid, "manutenzione", projectId)} tutte etichettaRiprendi="Apri il piano"
            testoDi={b => `Intervento di manutenzione non salvato${b.info?.pianoNome ? ` — piano «${b.info.pianoNome}»` : ""}${delGiorno(b.info?.data)}`}
            onRiprendi={(_, b) => { if (b.info?.impiantoId) setImpiantoSel(b.info.impiantoId); if (b.info?.pianoId) setPianoSel(b.info.pianoId); }} />
          {vistaImpianto==="componenti" ? (
            <>
              {!formComponenteAperto && (
                <button className="btn btn-primary" onClick={()=>setFormComponenteAperto({parentId:null,parentNome:null})} style={{marginBottom:16,display:"flex",alignItems:"center",gap:6}}>
                  <Plus size={14}/> Nuovo componente di primo livello
                </button>
              )}
              {formComponenteAperto?.parentId===null && (
                <FormComponente onAnnulla={()=>setFormComponenteAperto(null)}
                  onSalva={async (dati)=>{
                    await addDoc(collection(db,"manutenzioneComponenti"), {
                      organizationId: orgId, impiantoId: impiantoSel, parentId: null, ...dati, createdAt: serverTimestamp(),
                    });
                    addToast("Componente aggiunto ✅","success");
                    setFormComponenteAperto(null);
                  }}/>
              )}
              {componentiRadice.length===0 && !formComponenteAperto ? (
                <div style={{textAlign:"center",padding:40,color:"#9ca3af"}}>
                  Nessun componente ancora censito.<br/>
                  <span style={{fontSize:12}}>Es. "Campo FV 1", "Inverter INV-01", "Quadro QGBT"... poi aggiungi i sotto-elementi (stringhe, sensori, ecc.).</span>
                </div>
              ) : (
                <div style={{background:"#fff",border:"1px solid #e5e7eb",borderRadius:10,padding:14}}>
                  {componentiRadice.map(c=>(
                    <NodoComponente key={c.id} componente={c} tutti={componenti} livello={0}
                      espansi={componenteEspanso} setEspansi={setComponenteEspanso}
                      onAggiungiFiglio={(parentId,parentNome)=>setFormComponenteAperto({parentId,parentNome})}
                      onElimina={eliminaComponente} onVediStorico={setStoricoComponente}
                      formAperto={formComponenteAperto}
                      onSalvaForm={async (dati)=>{
                        await addDoc(collection(db,"manutenzioneComponenti"), {
                          organizationId: orgId, impiantoId: impiantoSel, parentId: formComponenteAperto.parentId, ...dati, createdAt: serverTimestamp(),
                        });
                        addToast("Componente aggiunto ✅","success");
                        setFormComponenteAperto(null);
                      }}
                      onAnnullaForm={()=>setFormComponenteAperto(null)}/>
                  ))}
                </div>
              )}
            </>
          ) : (
            <>
              {!mostraFormPiano && (
                <button className="btn btn-primary" onClick={()=>{setMostraFormPiano(true);setModalitaPianoComponenti(false);}} style={{marginBottom:16,display:"flex",alignItems:"center",gap:6}}>
                  <Plus size={14}/> Nuovo Piano di Manutenzione
                </button>
              )}
              {mostraFormPiano && !modalitaPianoComponenti && (
                <>
                  <FormPiano orgId={orgId} onAnnulla={()=>setMostraFormPiano(false)}
                    onSalva={async (dati)=>{
                      await addDoc(collection(db,"manutenzionePiani"), {
                        organizationId: orgId, impiantoId: impiantoSel, projectId, ...dati, createdAt: serverTimestamp(),
                      });
                      addToast("Piano di Manutenzione creato ✅","success");
                      setMostraFormPiano(false);
                    }}/>
                  <div style={{marginTop:-10,marginBottom:16}}>
                    <button onClick={()=>setModalitaPianoComponenti(true)} style={{fontSize:11,color:"#6b7280",background:"none",border:"none",cursor:"pointer",textDecoration:"underline"}}>
                      Preferisci tracciare le singole apparecchiature una per una? Passa alla modalità ad albero componenti
                    </button>
                  </div>
                </>
              )}
              {mostraFormPiano && modalitaPianoComponenti && (
                <>
                  <FormPianoComponenti orgId={orgId} onAnnulla={()=>setMostraFormPiano(false)}
                    onSalva={async (dati)=>{
                      await addDoc(collection(db,"manutenzionePiani"), {
                        organizationId: orgId, impiantoId: impiantoSel, projectId, ...dati, createdAt: serverTimestamp(),
                      });
                      addToast("Piano di Manutenzione creato ✅","success");
                      setMostraFormPiano(false);
                    }}/>
                  <div style={{marginTop:-10,marginBottom:16}}>
                    <button onClick={()=>setModalitaPianoComponenti(false)} style={{fontSize:11,color:"#6b7280",background:"none",border:"none",cursor:"pointer",textDecoration:"underline"}}>
                      ← Torna al capitolato di manutenzione
                    </button>
                  </div>
                </>
              )}
              {piani.length===0 && !mostraFormPiano ? (
                <div style={{textAlign:"center",padding:40,color:"#9ca3af"}}>Nessun piano di manutenzione ancora creato per questo impianto.</div>
              ) : (
                <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(240px,1fr))",gap:12}}>
                  {piani.map(p=>{
                    const isCap = p.modalita === "capitolato";
                    // Interventi reali del piano (prima si passava un elenco vuoto: la scadenza
                    // mostrata partiva sempre dalla decorrenza e il piano risultava scaduto
                    // anche dopo le visite fatte).
                    const ivPiano = interventiImpianto.filter(iv=>iv.pianoId===p.id).sort((a,b)=>(b.data||"").localeCompare(a.data||""));
                    const scadenza = isCap ? prossimaScadenzaCapitolato(p, ivPiano) : prossimaScadenza(p, ivPiano[0]||null);
                    const scaduta = scadenza && scadenza < new Date();
                    return (
                      <div key={p.id} onClick={()=>setPianoSel(p.id)}
                        style={{background:"#fff",border:`1px solid ${scaduta?"#fca5a5":"#e5e7eb"}`,borderRadius:10,padding:14,cursor:"pointer"}}>
                        <div style={{fontSize:13,fontWeight:700,marginBottom:4}}>{p.nome}</div>
                        <div style={{fontSize:11,color:"#9ca3af",marginBottom:6}}>
                          {isCap ? `📋 Capitolato — ${(p.voci||[]).filter(v=>!v.np).length} voci${(p.voci||[]).some(v=>v.np)?` (+${(p.voci||[]).filter(v=>v.np).length} N.P.)`:""}` : (FREQUENZE[p.frequenza]?.label||p.frequenza)}
                        </div>
                        {scadenza && (
                          <div style={{fontSize:11,color:scaduta?"#dc2626":"#6b7280",fontWeight:scaduta?700:400}}>
                            {scaduta?"⚠️ ":"📅 "}Prossima: {scadenza.toLocaleDateString("it-IT")}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>

        {storicoComponente && (() => {
          const storico = interventiImpianto
            .map(iv => ({ iv, gruppo: (iv.checklistCompilata||[]).find(g=>g.componenteId===storicoComponente.id) }))
            .filter(r => r.gruppo)
            .sort((a,b)=>(b.iv.data||"").localeCompare(a.iv.data||""));
          return (
            <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&setStoricoComponente(null)}>
              <div className="modal" style={{maxWidth:480,maxHeight:"85vh",overflowY:"auto"}}>
                <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:12}}>
                  <h3 style={{fontSize:15,fontWeight:700}}>📜 Storico — {storicoComponente.nome}</h3>
                  <button className="btn-icon" onClick={()=>setStoricoComponente(null)}><X size={16}/></button>
                </div>
                {storico.length===0 ? (
                  <div style={{textAlign:"center",padding:30,color:"#9ca3af",fontSize:13}}>Nessun intervento ha ancora interessato questo componente.</div>
                ) : storico.map(({iv,gruppo})=>(
                  <div key={iv.id} style={{border:"1px solid #e5e7eb",borderRadius:8,padding:12,marginBottom:10}}>
                    <div style={{display:"flex",justifyContent:"space-between",marginBottom:6}}>
                      <span style={{fontSize:13,fontWeight:700}}>{iv.data.split("-").reverse().join("/")}</span>
                      <span style={{fontSize:11,color:"#6b7280"}}>{iv.tecnicoNome||"—"} · {iv.esito||"—"}</span>
                    </div>
                    {gruppo.voci.map(v=>(
                      <div key={v.id} style={{fontSize:12,color:v.fatto?"#059669":"#dc2626",display:"flex",alignItems:"center",gap:6}}>
                        {v.fatto?"✅":"❌"} {v.testo}
                      </div>
                    ))}
                    {iv.note && <div style={{fontSize:11,color:"#9ca3af",marginTop:6,fontStyle:"italic"}}>"{iv.note}"</div>}
                  </div>
                ))}
              </div>
            </div>
          );
        })()}
      </div>
    );
  }

  // ── Livello 1: Impianti della commessa ──────────────────────────────────
  return (
    <div style={{display:"flex",flexDirection:"column",height:"100%",overflow:"hidden"}}>
      <div style={{background:"#3C3489",padding:"14px 24px",flexShrink:0}}>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between"}}>
          <h2 style={{fontSize:17,fontWeight:700,display:"flex",alignItems:"center",gap:8,color:"#fff",margin:0}}>
            <Wrench size={17} color="#fff"/> Gestione Manutenzione
          </h2>
          <button onClick={()=>setVistaDashboard(true)}
            style={{display:"flex",alignItems:"center",gap:6,padding:"6px 14px",border:"1px solid rgba(255,255,255,.3)",borderRadius:8,background:"rgba(255,255,255,.1)",color:"#fff",fontSize:12,fontWeight:700,cursor:"pointer"}}>
            📊 Dashboard
          </button>
        </div>
        <p style={{fontSize:12,color:"#CECBF6",margin:"4px 0 0"}}>{project?.codiceCommessa?`[${project.codiceCommessa}] `:""}{project?.name}</p>
      </div>
      <div style={{flex:1,overflowY:"auto",padding:20}}>
        {/* interventi iniziati e non salvati su questo dispositivo: si apre il loro piano */}
        <ElencoBozze prefisso={prefissoBozze(currentUser?.uid, "manutenzione", projectId)} tutte etichettaRiprendi="Apri il piano"
          testoDi={b => `Intervento di manutenzione non salvato${b.info?.pianoNome ? ` — piano «${b.info.pianoNome}»` : ""}${delGiorno(b.info?.data)}`}
          onRiprendi={(_, b) => { if (b.info?.impiantoId) setImpiantoSel(b.info.impiantoId); if (b.info?.pianoId) setPianoSel(b.info.pianoId); }} />
        {!mostraFormImpianto && (
          <button className="btn btn-primary" onClick={()=>setMostraFormImpianto(true)} style={{marginBottom:16,display:"flex",alignItems:"center",gap:6}}>
            <Plus size={14}/> Nuovo impianto
          </button>
        )}
        {mostraFormImpianto && (
          <FormImpianto nomeSuggerito={project?.name||""} onAnnulla={()=>setMostraFormImpianto(false)}
            impiantiAnagrafica={impiantiAnagrafica} clienteNome={project?.clienteNome||""}
            onSalva={async ({ creaInAnagrafica, ...dati })=>{
              let impiantoAnagraficaId = dati.impiantoAnagraficaId || null;
              // Impianto nuovo: nasce PRIMA in anagrafica, poi lo si aggancia qui. Così
              // resta disponibile per le commesse future e per le verifiche di legge,
              // invece di restare sepolto dentro questa singola commessa.
              if (creaInAnagrafica && project?.clienteId) {
                const ref = await addDoc(collection(db,"impianti"), {
                  organizationId: orgId,
                  clienteId: project.clienteId,
                  clienteNome: project.clienteNome || "",
                  denominazione: dati.nome,
                  tipologia: dati.tipo || "",
                  // la sede dei lavori della commessa diventa la sede dell'impianto
                  sede: project?.sede || null,
                  ubicazione: "", indirizzo: project?.sede?.indirizzo || "", matricola: "", costruttore: "",
                  annoInstallazione: "", potenzaKw: "", tensione: "",
                  referenteSito: project?.sede?.referente || "", telefonoSito: project?.sede?.telefono || "",
                  note: `Creato dalla commessa ${project?.name||""}`,
                  statoImpianto: "attivo",
                  createdAt: serverTimestamp(),
                });
                impiantoAnagraficaId = ref.id;
              }
              await addDoc(collection(db,"manutenzioneImpianti"), {
                organizationId: orgId, projectId, ...dati,
                impiantoAnagraficaId, createdAt: serverTimestamp(),
              });
              addToast(impiantoAnagraficaId
                ? (creaInAnagrafica ? "Impianto creato e aggiunto all'anagrafica ✅" : "Impianto collegato ✅")
                : "Impianto creato ✅","success");
              setMostraFormImpianto(false);
            }}/>
        )}
        {impianti.length===0 && !mostraFormImpianto ? (
          <div style={{textAlign:"center",padding:40,color:"#9ca3af"}}>Nessun impianto ancora registrato per questa commessa.</div>
        ) : (
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(220px,1fr))",gap:12}}>
            {impianti.map(i=>(
              <div key={i.id} onClick={()=>setImpiantoSel(i.id)}
                style={{background:"#fff",border:"1px solid #e5e7eb",borderRadius:10,padding:14,cursor:"pointer"}}>
                <div style={{fontSize:13,fontWeight:700}}>{i.nome}</div>
                {i.tipo && <div style={{fontSize:11,color:"#9ca3af",marginTop:2}}>{i.tipo}</div>}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Dashboard Manutenzione (sull'intera commessa) ───────────────────────────
function DashboardManutenzione({ orgId, projectId, project, onVaiAlPiano, onChiudi }) {
  const [impianti, setImpianti] = useState([]);
  const [piani, setPiani] = useState([]);
  const [interventi, setInterventi] = useState([]);

  useEffect(() => {
    if (!orgId || !projectId) return;
    return onSnapshot(query(collection(db,"manutenzioneImpianti"), where("organizationId","==",orgId), where("projectId","==",projectId)),
      snap => setImpianti(snap.docs.map(d=>({id:d.id,...d.data()}))));
  }, [orgId, projectId]);

  useEffect(() => {
    if (!orgId || !projectId) return;
    return onSnapshot(query(collection(db,"manutenzionePiani"), where("organizationId","==",orgId), where("projectId","==",projectId)),
      snap => setPiani(snap.docs.map(d=>({id:d.id,...d.data()}))));
  }, [orgId, projectId]);

  useEffect(() => {
    if (!orgId || !projectId) return;
    return onSnapshot(query(collection(db,"manutenzioneInterventi"), where("organizationId","==",orgId), where("projectId","==",projectId)),
      snap => setInterventi(snap.docs.map(d=>({id:d.id,...d.data()}))));
  }, [orgId, projectId]);

  const oggiStr = toLocalDateStr(new Date());
  const tra7 = new Date(); tra7.setDate(tra7.getDate()+7);
  const tra30 = new Date(); tra30.setDate(tra30.getDate()+30);

  const righe = piani.map(p=>{
    const interventiPiano = interventi.filter(i=>i.pianoId===p.id).sort((a,b)=>(b.data||"").localeCompare(a.data||""));
    const ultimo = interventiPiano[0];
    const scadenza = p.modalita==="capitolato" ? prossimaScadenzaCapitolato(p, interventiPiano) : prossimaScadenza(p, ultimo);
    const impianto = impianti.find(i=>i.id===p.impiantoId);
    return { piano:p, scadenza, impiantoNome:impianto?.nome||"—" };
  }).filter(r=>r.scadenza);

  const scadute = righe.filter(r=>r.scadenza < new Date(oggiStr+"T00:00:00"));
  const oggi = righe.filter(r=>toLocalDateStr(r.scadenza)===oggiStr);
  const settimana = righe.filter(r=>r.scadenza>=new Date() && r.scadenza<=tra7);
  const mese = righe.filter(r=>r.scadenza>=new Date() && r.scadenza<=tra30);

  const KPI = ({label, valore, colore}) => (
    <div style={{background:"#fff",border:`1px solid ${colore?"#fca5a5":"#e5e7eb"}`,borderRadius:10,padding:14}}>
      <div style={{fontSize:24,fontWeight:800,color:colore||"#111827"}}>{valore}</div>
      <div style={{fontSize:11,color:"#6b7280",marginTop:2}}>{label}</div>
    </div>
  );

  const ListaRighe = ({lista, vuoto}) => lista.length===0 ? (
    <div style={{fontSize:12,color:"#9ca3af",padding:"10px 0"}}>{vuoto}</div>
  ) : (
    <div>
      {lista.map(r=>(
        <div key={r.piano.id} onClick={()=>onVaiAlPiano(r.piano.impiantoId, r.piano.id)}
          style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"8px 0",borderBottom:"1px solid #f3f4f6",cursor:"pointer"}}>
          <div>
            <div style={{fontSize:13,fontWeight:600}}>{r.piano.nome}</div>
            <div style={{fontSize:11,color:"#9ca3af"}}>{r.impiantoNome}</div>
          </div>
          <div style={{fontSize:12,color:"#6b7280"}}>{r.scadenza.toLocaleDateString("it-IT")}</div>
        </div>
      ))}
    </div>
  );

  return (
    <div style={{display:"flex",flexDirection:"column",height:"100%",overflow:"hidden"}}>
      <div style={{background:"#3C3489",padding:"14px 24px",flexShrink:0}}>
        <button onClick={onChiudi} style={{background:"none",border:"none",cursor:"pointer",color:"#CECBF6",fontSize:12,fontWeight:600,display:"flex",alignItems:"center",gap:4,marginBottom:6}}>
          <ChevronLeft size={14}/> Torna agli impianti
        </button>
        <h2 style={{fontSize:17,fontWeight:700,display:"flex",alignItems:"center",gap:8,color:"#fff",margin:0}}>
          <Wrench size={17} color="#fff"/> Dashboard Manutenzione
        </h2>
        <p style={{fontSize:12,color:"#CECBF6",margin:"4px 0 0"}}>{project?.name}</p>
      </div>
      <div style={{flex:1,overflowY:"auto",padding:20}}>
        <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:10,marginBottom:20}}>
          <KPI label="Scadute" valore={scadute.length} colore={scadute.length>0?"#dc2626":null}/>
          <KPI label="Previste oggi" valore={oggi.length}/>
          <KPI label="Entro 7 giorni" valore={settimana.length}/>
          <KPI label="Entro 30 giorni" valore={mese.length}/>
        </div>
        <div style={{background:"#fff",border:"1px solid #fca5a5",borderRadius:10,padding:14,marginBottom:14}}>
          <div style={{fontSize:13,fontWeight:700,color:"#dc2626",marginBottom:6}}>⚠️ Manutenzioni scadute</div>
          <ListaRighe lista={scadute} vuoto="Nessuna manutenzione scaduta — tutto in regola."/>
        </div>
        <div style={{background:"#fff",border:"1px solid #e5e7eb",borderRadius:10,padding:14}}>
          <div style={{fontSize:13,fontWeight:700,color:"#374151",marginBottom:6}}>📅 In scadenza entro 30 giorni</div>
          <ListaRighe lista={mese} vuoto="Nessuna manutenzione in scadenza nel prossimo mese."/>
        </div>
      </div>
    </div>
  );
}
